/**
 * Hand-traced real circuits, as an alternative to the daily procedural layout.
 *
 * Each `points` list is the circuit's own centerline, in metres, surveyed from
 * OpenStreetMap's raceway ways and route relations (ODbL) and resampled to a
 * control point every 6-40 m — dense through a hairpin, sparse down a
 * straight, adapted to the local radius rather than fixed. `x` runs east and
 * `y` runs south, so the stored ring draws north-up on a canvas whose y grows
 * downward. Index 0 is the start/finish line — the traced way's own seam, not
 * always the precise real-world line — and the order is the real racing
 * direction: most of these run clockwise, but Interlagos genuinely runs the
 * other way, and this list does not straighten that out.
 *
 * Two of these — Sochi and Long Beach — have no usable OpenStreetMap raceway
 * geometry (Sochi's site is mapped as disconnected service-road fragments;
 * Long Beach is ordinary public streets, closed only on race weekend, never
 * tagged as a raceway) and are built from the published turn sequence instead
 * of a GPS trace. Their blurbs say so.
 *
 * Suzuka's back straight crosses back over its own front straight at grade in
 * this trace — the real circuit does that on a flyover, which this renderer
 * has no notion of, so the crossing is flattened rather than bridged. It is
 * the one circuit here where {@link selfIntersects} would report true and
 * that is a feature of the real layout, not a bug in the trace.
 *
 * The numbers are plain decimal literals and nothing here reads a clock, a
 * random source or a trig function, so a named circuit regenerates
 * byte-identically forever exactly as a dated one does.
 *
 * `length` is what the circuit is scaled to in game, not its real size. A kart
 * cannot take a Formula 1 hairpin at full scale: {@link MIN_RADIUS} is 6.5 m
 * and the tightest corner on Spa (La Source, 14.3 m) would come out at 4 m if
 * the lap were shortened to the daily 820 m. Each `length` is therefore the
 * real lap scaled until its tightest corner is a radius a kart can hold, with
 * a floor well above the daily 820 m even for the tightest of these — which is
 * why the scale from real lap to game lap varies more across this set (0.2 to
 * 0.59) than it did across the original three (0.35 to 0.42): some of these
 * circuits are simply more technical, corner-for-corner, than Monza or Spa.
 *
 * `elevation`, `banking`, `verticalScale`, `runoff`, `runoffDefault` and
 * `wallMargin` are the vertical shape and run-off character researched in
 * docs/circuit-fidelity.md — first-pass figures meant to be refined by feel,
 * not surveyed data. All five are optional and independent of the trace
 * above: an unauthored circuit stays exactly as flat and uniformly grassed as
 * it always was.
 */

import { type RunoffKind, type SeriesKeyframe } from './elevation';
import type { LandmarkSpec } from './landmarks';

export interface RealCircuit {
  id: string;
  /** Short name — what the chip and the circuit title show. */
  name: string;
  fullName: string;
  country: string;
  /** The real lap, in metres. Shown so the scaling is not a secret. */
  realLength: number;
  /** The in-game centerline length this is scaled to. See the note above. */
  length: number;
  /** One line of what the lap is like, for the chip's tooltip. */
  blurb: string;
  /**
   * Height keyframes, `[fraction of lap 0..1, real-world metres]`, wrapping
   * across the start/finish seam. Real-world scale like `points` — the build
   * pipeline applies the horizontal shrink and `verticalScale` together, see
   * docs/circuit-fidelity.md §2. Omit for an unauthored (flat) circuit.
   */
  elevation?: readonly SeriesKeyframe[];
  /**
   * Cross-slope keyframes, `[fraction of lap 0..1, rise ÷ run]`. Positive
   * tilts the inside of the corner down. Already the *reduced* value to use
   * in game (see docs/circuit-fidelity.md §5's Zandvoort note) — not a raw
   * real-world angle, and not scaled further.
   */
  banking?: readonly SeriesKeyframe[];
  /**
   * Extra vertical shrink beyond the horizontal `length` scaling, applied to
   * `elevation` only. Omit (= 1) to keep the real gradient — see
   * docs/circuit-fidelity.md §2 and §5 for which circuits need this and why.
   */
  verticalScale?: number;
  /**
   * Run-off surface beyond the kerb where it differs from `runoffDefault`.
   * Spans are `[fromFraction, toFraction, kind]`; a `to` less than `from`
   * wraps through the start/finish line. Later spans win where two overlap.
   */
  runoff?: readonly (readonly [number, number, RunoffKind])[];
  /** Run-off surface everywhere `runoff` doesn't override. Omit for grass. */
  runoffDefault?: RunoffKind;
  /**
   * Barrier distance past the kerb, overriding the daily generator's
   * `WALL_MARGIN` for circuits where the real run-off is dramatically
   * narrower (a street circuit) or wider (Paul Ricard) than that default.
   */
  wallMargin?: number;
  /**
   * Kerb colours, cycled in order along the kerb, one entry per ~6 m block.
   * Omit for the default red/white every circuit used to have — that is still
   * what most of them genuinely are, and an unauthored circuit keeps it.
   *
   * Two entries reproduce the old alternation exactly; more than two are for
   * the circuits whose kerbs are a recognisable part of how they look, Spa's
   * red/white/blue above all. Like `elevation`, these are set by eye from how
   * a circuit reads on television rather than sampled from a survey, and are
   * meant to be adjusted by feel.
   *
   * Ignored when the colourblind palette is on: kerbs mark the track limit,
   * which is information, and the accessible palette has to win over a
   * circuit's own livery. See `docs/circuit-fidelity.md` §6.
   */
  kerbs?: readonly string[];
  /**
   * Scenery that identifies this circuit and no other — Zandvoort's sculpture,
   * Suzuka's ferris wheel. Placement, sizing and the reasoning behind both are
   * in `src/track/landmarks.ts`; omit for a circuit with nothing to place.
   */
  landmarks?: readonly LandmarkSpec[];
  /** Traced centerline at real scale; index 0 is the start/finish line. */
  points: readonly (readonly [number, number])[];
}

/** Track keys for these circuits are `circuit:<id>` — see {@link circuitForKey}. */
export const CIRCUIT_KEY_PREFIX = 'circuit:';

export const REAL_CIRCUITS: readonly RealCircuit[] = [
  {
    id: 'monza',
    name: 'Monza',
    fullName: 'Autodromo Nazionale di Monza',
    country: 'Italy',
    realLength: 5793,
    length: 2020,
    blurb:
      'Two chicanes, two Lesmos and Ascari strung between the longest straights in racing; Parabolica keeps opening all the way onto the pit straight.',
    elevation: [[0.2, 0], [0.35, 3.0], [0.5, 0], [0.5, 0], [0.65, -3.0], [0.8, 0]],
    // Il Tricolore: Monza paints its kerbs green, white and red.
    kerbs: ['#009246', '#e9e9ec', '#ce2b37'],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
      { kind: 'banking', name: 'Curva Sopraelevata', at: 0.83, offset: -46, scale: 1 },
    ],
    points: [
      [-413.2, 603.6], [-409.1, 563.8], [-405.1, 524], [-401, 484.2], [-396.3, 444.5],
      [-391.6, 404.8], [-388.1, 365], [-384.5, 325.1], [-381, 285.3], [-377.5, 245.5],
      [-373.9, 205.6], [-370.4, 165.8], [-366.8, 125.9], [-363.3, 86.1], [-359.8, 46.3],
      [-356.2, 6.4], [-352.7, -33.4], [-349.1, -73.2], [-345.6, -113.1], [-344.2, -129],
      [-343.4, -135], [-340.4, -140], [-335, -142.6], [-329.1, -142.5], [-321.5, -139.9],
      [-315.8, -138.2], [-309.9, -138.1], [-304.5, -140.7], [-300.9, -145.4], [-299.1, -153.1],
      [-301.1, -164.9], [-311, -203.6], [-321.2, -242.3], [-328.2, -281.7], [-330, -321.6],
      [-326.9, -361.5], [-323.8, -401.3], [-320.6, -441.2], [-315.3, -480.8], [-304.9, -519.3],
      [-289.1, -556.1], [-268.7, -590.4], [-243.5, -621.4], [-214.3, -648.7], [-182.1, -672.4],
      [-147.5, -692.4], [-111.1, -708.8], [-72.9, -720.7], [-33.9, -729.6], [5.6, -735.3],
      [45.5, -739], [85.3, -742.1], [125.2, -745.3], [165.1, -748.5], [204.9, -751.5],
      [244.8, -754.4], [284.8, -756.6], [324.7, -758.6], [364.6, -760.5], [401, -770.6],
      [404.8, -782], [408.2, -793.5], [411.5, -800.8], [417.5, -805.8], [427.2, -808],
      [466.5, -815.4], [505, -826.2], [543, -838.7], [581, -851.2], [619, -863.7], [657, -876],
      [695.1, -888.3], [734.3, -894.1], [764.2, -883.6], [785.9, -863.2], [797.7, -835.8],
      [802.1, -796.1], [805.8, -756.3], [809.5, -716.4], [813.2, -676.6], [816.9, -636.8],
      [820.5, -597], [819.2, -579.1], [812.6, -567], [799.6, -557.6], [764.7, -538.2],
      [729.7, -518.8], [694.7, -499.4], [659.7, -480.1], [624.7, -460.7], [589.7, -441.3],
      [554.8, -421.9], [519.8, -402.6], [485.6, -381.7], [452.9, -358.8], [421.8, -333.7],
      [391.7, -307.4], [361.5, -281.1], [331.3, -254.9], [301.1, -228.7], [270.9, -202.4],
      [240.7, -176.2], [210.5, -150], [180.4, -123.7], [150.2, -97.5], [120, -71.3], [89.8, -45],
      [59.6, -18.8], [29.4, 7.4], [-0.8, 33.7], [-30.1, 60.7], [-34.6, 71.7], [-34.2, 85.6],
      [-30.7, 125.4], [-37.8, 164.5], [-59.2, 198], [-90, 223.3], [-98.9, 236.5], [-102.8, 256],
      [-107.7, 295.7], [-112.6, 335.4], [-117.5, 375.1], [-122.5, 414.8], [-127.3, 454.5],
      [-131.5, 494.3], [-135.7, 534], [-139.8, 573.8], [-144, 613.6], [-148.2, 653.4],
      [-152.4, 693.1], [-156.6, 732.9], [-160.7, 772.7], [-164.9, 812.5], [-169, 852.2],
      [-173.2, 892], [-177.3, 931.8], [-181.4, 971.6], [-185.6, 1011.4], [-189.7, 1051.1],
      [-193.9, 1090.9], [-198, 1130.7], [-202.1, 1170.5], [-208.2, 1209.9], [-220.4, 1230.4],
      [-242.9, 1249.9], [-280.7, 1261.8], [-312.2, 1257.2], [-348.5, 1240.7], [-378.9, 1214.9],
      [-402.6, 1182.8], [-419.3, 1146.5], [-429.7, 1108], [-434.8, 1068.3], [-435.6, 1028.3],
      [-436.4, 988.3], [-437.2, 948.4], [-437.7, 908.4], [-437.3, 868.4], [-435.6, 828.4],
      [-432.1, 788.6], [-428.1, 748.8], [-424, 709], [-420, 669.2], [-415.9, 629.4],
    ],
  },
  {
    id: 'shanghai',
    name: 'Shanghai',
    fullName: 'Shanghai International Circuit',
    country: 'China',
    realLength: 5451,
    length: 2300,
    blurb:
      'Turn 1 spirals shut for a quarter of a mile, then a kilometre-long back straight runs into a hairpin that does the same thing again.',
    elevation: [[0.02, 0], [0.08, 2.0], [0.14, 0], [0.11, 0], [0.16, -1.5], [0.21, 0]],
    runoff: [[0.93, 0.98, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true, roof: 'lotus' },
    ],
    points: [
      [49.4, 278.6], [10.5, 287.7], [-28.5, 296.8], [-67.4, 305.9], [-106.4, 315], [-145.3, 324.1],
      [-184.3, 333.2], [-223.3, 342.2], [-262.2, 351.2], [-301.2, 360.2], [-340.7, 365.1],
      [-378.8, 354], [-410.3, 329.8], [-423.6, 307.5], [-430.2, 280.5], [-427.8, 250.7],
      [-417.9, 229.1], [-401.8, 214.3], [-381.2, 207.1], [-363.3, 207.2], [-346.6, 213.5],
      [-336.8, 223.3], [-332.7, 234.5], [-334.2, 248.3], [-343.9, 278.6], [-341.8, 292.4],
      [-334.4, 304], [-322.6, 311.3], [-311, 314], [-299.4, 311.7], [-289.5, 305.2],
      [-278.8, 290.7], [-267.7, 263], [-265.8, 229.4], [-276.1, 203.6], [-292.6, 183.6],
      [-318.8, 153.4], [-345.1, 123.2], [-371.3, 93], [-397.7, 62.9], [-424.4, 33.1],
      [-451.1, 3.4], [-470.6, -24.3], [-484.2, -61.9], [-497.3, -99.7], [-510.4, -137.4],
      [-523.5, -175.2], [-536.7, -213], [-542.2, -230.1], [-542.7, -238], [-540.3, -245.5],
      [-534.9, -251.2], [-527.5, -254.1], [-517.8, -252.9], [-505.2, -246.8], [-473.2, -222.8],
      [-445.5, -194], [-423.2, -160.9], [-403.5, -126.1], [-383.3, -91.6], [-363.1, -57.1],
      [-343, -22.5], [-319.6, 9.8], [-292.5, 30.3], [-263.2, 42.5], [-223.5, 46.6], [-190.3, 40.2],
      [-160.2, 24.7], [-131.2, -2.6], [-112.3, -35.4], [-95.7, -71.8], [-77.4, -107.3],
      [-58.6, -127.8], [-33.8, -140.6], [-4.3, -144.2], [21, -138.9], [48, -122.1], [67.1, -96.7],
      [84.5, -60.6], [103.2, -25.4], [111.3, -19.8], [125, -17.6], [138.2, -21.6], [150.1, -32],
      [174.9, -63.5], [192, -94.3], [190, -108], [179.5, -127.3], [158.3, -161.2], [137.2, -195.1],
      [116.4, -229.3], [95.7, -263.5], [74.9, -297.7], [54, -331.9], [33.1, -366], [12.2, -400.1],
      [-8.7, -434.2], [-29.5, -468.3], [-53.6, -499.2], [-61.6, -499.2], [-70.5, -495],
      [-81.4, -483.4], [-92.9, -467], [-104.3, -459], [-115.7, -455.8], [-127.6, -457],
      [-139.8, -463.7], [-156.1, -481.1], [-167.6, -504.2], [-171.2, -543.7], [-161.8, -572],
      [-147.3, -591], [-125.9, -605.3], [-87.1, -614.2], [-47.6, -610], [-18.3, -597.6],
      [12, -571.8], [35.3, -539.3], [57.4, -506], [79.4, -472.6], [101.5, -439.3], [123.6, -405.9],
      [145.7, -372.6], [167.8, -339.2], [189.9, -305.9], [212, -272.5], [234, -239.2],
      [256.1, -205.8], [278.2, -172.5], [300.4, -139.2], [322.6, -105.9], [344.7, -72.6],
      [366.9, -39.3], [389, -6], [411.1, 27.4], [433.2, 60.7], [455.2, 94.1], [477.3, 127.4],
      [499.4, 160.8], [521.5, 194.1], [543.7, 227.4], [565.8, 260.7], [588, 294], [610.2, 327.3],
      [632.3, 360.6], [654.5, 393.9], [676.6, 427.2], [681.9, 435.7], [684.5, 441.1],
      [686.3, 446.8], [684.8, 452.4], [680.1, 456.1], [674.2, 455.9], [666.6, 453.4],
      [649.3, 443.4], [615, 422.9], [597.2, 404.1], [573.8, 371.6], [550.4, 339.2], [527, 306.8],
      [503.5, 274.4], [480.1, 241.9], [456.6, 209.6], [440.1, 189.6], [432.7, 187.4], [422.8, 189],
      [384, 198.6], [345.2, 208.3], [306.4, 218], [267.6, 227.7], [228.6, 236.7], [189.6, 245.8],
      [150.7, 254.9], [111.7, 264], [72.8, 273.1],
    ],
  },
  {
    id: 'spa',
    name: 'Spa-Francorchamps',
    fullName: 'Circuit de Spa-Francorchamps',
    country: 'Belgium',
    realLength: 7004,
    length: 2930,
    blurb:
      'La Source into Eau Rouge and the climb up Kemmel, Pouhon taken blind and downhill, then Blanchimont flat out to the Bus Stop.',
    elevation: [[0.01, 0], [0.06, 20], [0.11, 0], [0.08, 0], [0.2, 34], [0.32, 0], [0.33, 0], [0.45, -15], [0.57, 0], [0.5, 0], [0.62, -30], [0.74, 0], [0.72, 0], [0.8, 12], [0.88, 0], [0.86, 0], [0.92, -10], [0.98, 0]],
    verticalScale: 0.5,
    runoff: [[0.02, 0.06, 'gravel'], [0.07, 0.1, 'gravel'], [0.23, 0.27, 'gravel'], [0.73, 0.77, 'gravel'], [0.83, 0.87, 'gravel']],
    // Red and yellow, the colours of Wallonia — Spa switched its kerbing over
    // from red/white in 2017 and it is now one of the circuit's tells.
    kerbs: ['#c8352f', '#f2c200'],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.93, offset: 28, roomier: true },
      { kind: 'tower', name: 'Pit and timing tower', at: 0.995, offset: 34, scale: 1 },
    ],
    points: [
      [-214.7, -819.7], [-234.7, -854.4], [-254.6, -889.1], [-274.5, -923.8], [-294.4, -958.5],
      [-314.2, -993.2], [-334.1, -1027.9], [-354, -1062.6], [-373.8, -1097.4], [-393.7, -1132.1],
      [-412, -1167.3], [-410.9, -1173.1], [-407.7, -1178.1], [-402.4, -1180.2], [-396.6, -1180.5],
      [-385.4, -1176.1], [-348.8, -1160], [-312.7, -1142.8], [-277.4, -1124], [-242.2, -1105.1],
      [-208.1, -1084.2], [-175.6, -1060.8], [-144.8, -1035.3], [-115.7, -1007.9], [-88.8, -978.3],
      [-62.6, -948.1], [-36.5, -917.8], [-10.5, -887.4], [15.4, -856.9], [41.3, -826.4],
      [67.1, -795.9], [94.2, -766.5], [119.2, -750], [152.6, -728], [173.8, -704.3],
      [190.9, -668.2], [199.3, -629.2], [205.9, -589.8], [222.1, -557.8], [243.7, -524.1],
      [265.3, -490.5], [286.9, -456.8], [308.5, -423.2], [330.4, -389.7], [353, -356.6],
      [372.3, -321.7], [387, -284.5], [397.3, -245.9], [408.3, -207.4], [419.3, -169],
      [430.4, -130.5], [441.4, -92.1], [452.5, -53.6], [463.5, -15.2], [474.6, 23.2],
      [485.6, 61.7], [496.6, 100.1], [507.6, 138.6], [518.6, 177.1], [529.6, 215.5], [540.6, 254],
      [551.6, 292.4], [562.6, 330.9], [573.6, 369.3], [585.1, 407.6], [595.4, 446.3], [594.2, 464],
      [583.7, 483.1], [569.5, 493.9], [546.8, 510], [536.9, 527.2], [534.6, 544.9], [540.5, 574.3],
      [550.2, 613.1], [558.8, 652.1], [556.8, 667.8], [550.7, 682.5], [538.9, 695.9], [516, 712.1],
      [483.1, 734.7], [450.2, 757.5], [417.2, 780.2], [384.3, 802.9], [351.4, 825.7],
      [318.5, 848.4], [285, 870.2], [267.6, 873.8], [246.1, 869.8], [229.7, 858.7], [218.9, 839.7],
      [217.1, 817.9], [223.4, 799.2], [238.5, 783.3], [272.3, 762.1], [306.2, 740.8], [340.4, 720],
      [357, 705.8], [365.9, 686], [366.1, 666.2], [358.7, 643.3], [346, 605.4], [333.7, 567.4],
      [321.6, 529.2], [310.5, 490.8], [303, 451.5], [295.7, 412.2], [288.3, 372.9], [280.9, 333.6],
      [274.2, 294.2], [267.4, 254.7], [258.6, 215.8], [244.7, 191.6], [218.2, 167.4],
      [194.6, 156.7], [165, 152.8], [125.1, 149.7], [85.3, 152.3], [47.6, 165.2], [14.7, 187.7],
      [-11, 218.1], [-28.1, 254.2], [-43.2, 291.2], [-58.4, 328.3], [-73.5, 365.3], [-88.6, 402.3],
      [-103.7, 439.4], [-118.9, 476.4], [-133.8, 513.5], [-153, 548.3], [-171.4, 563.6],
      [-193.8, 571.9], [-221.5, 572.5], [-247.8, 563.4], [-282.2, 543], [-307.5, 537.9],
      [-334.9, 542.3], [-354.2, 552.6], [-372, 571.3], [-393.8, 604.9], [-415.6, 638.4],
      [-437.3, 672], [-458.6, 705.9], [-481.4, 738.6], [-497.8, 749.7], [-517.3, 753.5],
      [-534.9, 750.6], [-550.4, 741.6], [-582.9, 718.1], [-615.3, 694.7], [-647.3, 670.8],
      [-667.7, 643.8], [-678.6, 605.7], [-673.9, 566.2], [-659.8, 528.8], [-644.8, 491.7],
      [-625, 457], [-602.2, 424.1], [-576.3, 393.7], [-548.4, 365], [-519.7, 337.2],
      [-490.3, 310.1], [-457.8, 286.8], [-423.2, 266.9], [-387.6, 248.5], [-352.1, 230.1],
      [-316.6, 211.6], [-281.2, 193], [-246.8, 172.7], [-215.5, 147.9], [-187.4, 119.5],
      [-164.3, 86.8], [-145.6, 51.5], [-129, 15.1], [-112.6, -21.3], [-96.3, -57.9],
      [-81.2, -94.9], [-76.3, -134.4], [-83.5, -173.6], [-97.5, -211], [-111.8, -248.4],
      [-126.1, -285.7], [-140.4, -323.1], [-154.7, -360.4], [-166.7, -398.6], [-175.1, -437.6],
      [-180.8, -477.2], [-184.3, -517.1], [-187.4, -557], [-190.7, -596.8], [-194.2, -636.7],
      [-194.7, -644.6], [-193.3, -652.4], [-188.4, -658.7], [-181, -661.4], [-169, -660.5],
      [-147.1, -659.7], [-141.3, -661.1], [-136.7, -664.8], [-133.8, -670], [-133.1, -677.9],
      [-135.5, -685.4], [-143.4, -697], [-163.9, -731.3], [-183.8, -766], [-203.8, -800.7],
    ],
  },
  {
    id: 'sepang',
    name: 'Sepang',
    fullName: 'Sepang International Circuit',
    country: 'Malaysia',
    realLength: 5551,
    length: 1928,
    blurb:
      'Turn 1\'s double-apex right-hander opens onto a long back straight; the technical infield through 5-9 punishes any run wide before the final sweep back onto the front straight.',
    elevation: [[0.15, 0], [0.3, 5], [0.45, 0], [0.5, 0], [0.65, -5], [0.8, 0]],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true, roof: 'hibiscus' },
    ],
    points: [
      [472, -191.1], [491.8, -168.5], [511.5, -145.9], [531.3, -123.4], [551.1, -100.8],
      [570.8, -78.2], [590.6, -55.7], [610.4, -33.1], [630.1, -10.5], [645.3, 8],
      [648.5, 19.4], [639, 47.5], [627.1, 75.1], [615.1, 102.6], [607.1, 113.8],
      [578.6, 120.9], [548.6, 122.6], [518.7, 124.3], [488.7, 126], [458.8, 128.1],
      [428.9, 130.8], [399, 133.6], [369.3, 137], [339.5, 140.6], [309.7, 144.3],
      [279.9, 147.9], [250.1, 151.6], [220.4, 155.2], [190.6, 158.8], [160.8, 162.5],
      [143, 164.8], [133.9, 168.5], [127.7, 176.1], [126.8, 183.9], [131, 192.6],
      [156.2, 208.8], [181.9, 224.4], [206.7, 241.2], [223.3, 263.6], [230.9, 292.5],
      [232.1, 322.4], [229.4, 352.3], [222.2, 381.4], [210.3, 408.9], [195.4, 434.8],
      [179.2, 455], [169.4, 461.4], [149.8, 462.6], [124.4, 447.5], [101.2, 428.4],
      [78, 409.4], [54.8, 390.4], [31.6, 371.4], [8.4, 352.4], [-14.8, 333.4],
      [-38, 314.4], [-61.2, 295.4], [-78.4, 281.7], [-106.6, 273.1], [-132.4, 275.4],
      [-159.3, 288.6], [-185.7, 302.8], [-212.7, 315.8], [-241.1, 325.1], [-270.9, 327.4],
      [-300.7, 324.4], [-329.4, 316], [-355.8, 302], [-378.3, 282.3], [-395.5, 258.1],
      [-401.6, 239.3], [-398.8, 221.7], [-392.5, 211.7], [-365.3, 200.7], [-335.7, 195.4],
      [-306.2, 190.2], [-276.7, 184.9], [-247.1, 179.6], [-217.6, 174.4], [-188.1, 169.1],
      [-158.5, 163.9], [-129, 158.6], [-99.5, 153.4], [-69.9, 148.1], [-40.4, 142.9],
      [-10.9, 137.6], [18.7, 132.4], [48.2, 127.1], [77.7, 121.9], [107.3, 116.6],
      [136.8, 111.3], [166.3, 105.9], [195.8, 100.5], [225.3, 95.2], [254.9, 89.8],
      [284.4, 84.4], [313.9, 79.4], [343.5, 74.5], [373.1, 69.6], [402.6, 64.1],
      [432.1, 58.4], [461.5, 52.6], [477.4, 44.6], [489.9, 29.4], [492.9, 10],
      [482.4, -8.7], [465.3, -18.1], [435.4, -18.1], [405.5, -15.6], [375.6, -13.1],
      [345.7, -10.6], [315.8, -8], [285.9, -5.5], [256, -3], [226.1, -0.7],
      [196.2, 1.6], [166.3, 3.9], [136.4, 6.2], [106.5, 8.6], [76.6, 10.9],
      [46.7, 13.3], [16.8, 15.7], [-13.1, 18.1], [-43, 20.5], [-72.9, 22.9],
      [-102.8, 25.3], [-132.7, 27.7], [-162.6, 30.1], [-192.5, 32.4], [-222.4, 34.8],
      [-252.3, 37.2], [-282.2, 39.6], [-312.1, 42], [-342, 44.4], [-371.9, 46.8],
      [-401.8, 49.2], [-431.7, 51.6], [-461.7, 53.7], [-485.5, 52.4], [-499.8, 45.8],
      [-508.8, 32.8], [-510.1, 15.1], [-504.1, -1.8], [-492.9, -12.9], [-475.8, -17.2],
      [-446.9, -9.8], [-425.8, -3.6], [-414, -5.1], [-406.3, -14], [-403.6, -27.5],
      [-415.4, -54.6], [-433.5, -78.5], [-448, -104.7], [-454.8, -133.7], [-455.8, -163.6],
      [-453.8, -193.6], [-449.8, -223.2], [-440, -251.5], [-425.5, -277.7], [-406.7, -300.9],
      [-383.5, -319.8], [-357.4, -334.5], [-329.4, -345.2], [-300.8, -354.4], [-272.3, -363.6],
      [-243.8, -373.1], [-215.5, -382.9], [-187.2, -392.8], [-159.2, -403.8], [-131.5, -415.1],
      [-103.7, -426.4], [-75.9, -437.7], [-48.5, -449.8], [-21.4, -462.7], [5.8, -475.4],
      [22.5, -481.9], [36.4, -481.1], [44.8, -475.8], [53, -447.4], [58.5, -417.9],
      [64, -388.4], [69.5, -358.9], [75, -329.4], [80.5, -300], [86.1, -270.5],
      [92.4, -241.2], [105.2, -214.2], [124, -190.8], [147.7, -172.6], [175.2, -161.2],
      [204.9, -157], [234.6, -159.8], [262.7, -169.7], [287.1, -187.1], [306.9, -209.5],
      [330.2, -228.3], [357.8, -239.7], [387.4, -241.9], [416.6, -235.6], [442.8, -221],
      [464.2, -200.1],
    ],
  },
  {
    id: 'silverstone',
    name: 'Silverstone',
    fullName: 'Silverstone Circuit',
    country: 'United Kingdom',
    realLength: 5865,
    length: 1309,
    blurb:
      'Maggotts, Becketts and Chapel string together as one continuous direction change before Hangar Straight; Stowe, Vale and Club close the lap through the old airfield\'s perimeter.',
    elevation: [[0.43, 0], [0.55, -5], [0.67, 0], [0.6, 0], [0.7, 3], [0.8, 0]],
    runoff: [[0.75, 0.8, 'gravel'], [0.85, 0.9, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [23.1, 40.5], [46.6, 21.8], [70, 3.2], [93.5, -15.5], [116.9, -34.2],
      [140.4, -52.9], [164, -71.5], [178.8, -76.8], [190.6, -75.7], [200.7, -69.3],
      [211.9, -41.7], [220.7, -13.1], [229.6, 15.6], [235.8, 34.5], [245, 44.9],
      [256.1, 49.1], [267.6, 46.3], [285, 22.4], [296.4, -5.3], [305, -34.1],
      [311.4, -63.4], [314.7, -93.2], [314.9, -119.1], [310.1, -136.3], [289.8, -158.2],
      [267.7, -178.5], [245.9, -199.1], [223.7, -219.2], [201.5, -239.4], [179.3, -259.5],
      [157.1, -279.7], [134.8, -299.8], [112.6, -320], [90.4, -340.1], [68.2, -360.3],
      [45.9, -380.3], [23.6, -400.4], [1.3, -420.5], [-20.9, -440.7], [-43.2, -460.7],
      [-65.6, -480.7], [-88, -500.7], [-110.4, -520.6], [-132.9, -540.4], [-155.4, -560.2],
      [-179, -578.7], [-205.3, -587.7], [-235.2, -586.6], [-257.2, -577.3], [-270.4, -562.5],
      [-276, -533.3], [-278.8, -503.4], [-282.4, -475.7], [-298.4, -450.8], [-319, -438.8],
      [-338.8, -436.9], [-362.9, -445.9], [-382, -468.6], [-387.2, -493.7], [-378.1, -522],
      [-364.5, -548.7], [-351.2, -575.6], [-338, -602.5], [-324.6, -629.4], [-308.8, -654.8],
      [-288.9, -677.3], [-267.3, -698.1], [-243.6, -716.4], [-218, -732], [-190, -742.6],
      [-160.8, -749.1], [-131, -753], [-101.2, -756.1], [-71.3, -759], [-41.5, -761.8],
      [-11.6, -764.7], [18.3, -767.5], [48.1, -770.3], [78, -772.8], [107.9, -775.2],
      [137.8, -777.6], [167.7, -780], [197.6, -782.4], [227.5, -784.8], [257.5, -785.5],
      [286.1, -777.2], [311, -760.8], [329.7, -737.5], [340.9, -709.7], [350.4, -681.2],
      [358.7, -652.4], [366.7, -623.5], [373.6, -594.3], [379.3, -564.9], [384.3, -535.3],
      [387.5, -505.5], [389.9, -475.6], [391.9, -445.7], [393.8, -415.7], [395.2, -385.8],
      [396.4, -355.8], [397.3, -325.8], [398.8, -295.8], [404.2, -266.4], [415.5, -238.6],
      [430.7, -212.8], [444.1, -186.2], [444.1, -156.5], [434.2, -128.2], [423.8, -100.1],
      [413.5, -71.9], [407.6, -42.6], [408.8, -12.7], [420.1, 14.8], [438.4, 38.6],
      [454.2, 59.2], [462, 87.9], [458.2, 117.5], [442.4, 142.7], [418.7, 161],
      [393.3, 176.9], [367.2, 191.7], [343.2, 209.7], [325, 233.3], [310.5, 259.6],
      [296, 285.9], [281.5, 312.1], [267, 338.4], [252.6, 364.7], [238.4, 391.1],
      [224.5, 417.7], [210.5, 444.2], [196.6, 470.8], [182.5, 497.3], [168.4, 523.8],
      [154.3, 550.2], [140.3, 576.7], [126.2, 603.2], [112, 629.7], [97.7, 656],
      [83.3, 682.3], [69, 708.7], [54.1, 734.7], [38.6, 760.4], [22.6, 785.8],
      [6, 810.8], [-10.9, 835.5], [-28.4, 859.9], [-46.3, 883.9], [-68.1, 904.5],
      [-95.1, 916.9], [-124.9, 919.3], [-153.7, 911.7], [-178.7, 895.3], [-197, 871.8],
      [-208.8, 844.2], [-222.3, 817.4], [-237.6, 791.7], [-254.1, 766.6], [-272, 742.5],
      [-291.5, 719.7], [-312.2, 698], [-332.8, 676.3], [-352.8, 653.9], [-372.3, 631.1],
      [-391.7, 608.2], [-407.3, 590], [-415.5, 584.5], [-427.3, 584.8], [-450.2, 603.6],
      [-461.8, 614.5], [-480.8, 619.5], [-497.5, 613.7], [-518.7, 592.6], [-537.2, 569],
      [-551.6, 542.7], [-561.5, 514.4], [-566.9, 485], [-564, 467.3], [-546.3, 443.2],
      [-528, 419.4], [-509.8, 395.6], [-491.5, 371.9], [-473.2, 348.1], [-455, 324.3],
      [-436.7, 300.5], [-418.4, 276.7], [-400.2, 252.9], [-381.9, 229.1], [-363.6, 205.3],
      [-345.4, 181.5], [-327.1, 157.7], [-308.8, 134], [-290.4, 110.3], [-272.1, 86.5],
      [-257.6, 70], [-230.2, 58.6], [-200.4, 55.7], [-170.5, 57.9], [-140.8, 62.3],
      [-111.2, 67], [-81.6, 71.5], [-51.6, 71.2], [-22.4, 65], [5.1, 53.2],
    ],
  },
  {
    id: 'suzuka',
    name: 'Suzuka',
    fullName: 'Suzuka International Racing Course',
    country: 'Japan',
    realLength: 5795,
    length: 1563,
    blurb:
      'The only figure-eight on this list — the back straight crosses back over the front straight at grade here, since nothing in this renderer builds the real flyover. Esses, Degner, Spoon and 130R still flow exactly as they should.',
    elevation: [[-0.03, 0], [0.05, 8], [0.13, 0], [0.05, 0], [0.15, 14], [0.25, 0], [0.25, 0], [0.35, -10], [0.45, 0], [0.47, 0], [0.55, -6], [0.63, 0], [0.57, 0], [0.65, 7], [0.73, 0], [0.67, 0], [0.75, -9], [0.83, 0]],
    verticalScale: 0.8,
    runoffDefault: 'gravel',
    runoff: [[0.48, 0.53, 'runoffAsphalt'], [0.68, 0.72, 'runoffAsphalt']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
      { kind: 'wheel', name: 'Ferris wheel', at: 0.16, offset: -40, scale: 1 },
    ],
    wallMargin: 7,
    points: [
      [-60.4, 130.9], [-90.2, 133.5], [-120.1, 136.1], [-150, 138.7], [-179.9, 141.3],
      [-199.9, 142.3], [-210.8, 138], [-222.2, 111.1], [-228, 81.6], [-233.8, 52.2],
      [-239.7, 22.8], [-245.5, -6.6], [-251.3, -36.1], [-257.2, -65.5], [-263, -94.9],
      [-268.8, -124.4], [-273.8, -153.9], [-272.3, -183.7], [-262.2, -211.9], [-249.7, -239.1],
      [-237.5, -266.6], [-231.6, -281.4], [-232.1, -295], [-238.9, -304.7], [-250.1, -308.7],
      [-261.6, -306.3], [-280.6, -283.7], [-296, -258], [-312.1, -232.7], [-328.2, -207.3],
      [-344.2, -182], [-363.4, -158.9], [-384.9, -138], [-410.1, -121.9], [-437.9, -111.1],
      [-467.5, -106.7], [-497.4, -104.3], [-527.4, -104.8], [-557.2, -108.4], [-586.4, -114.9],
      [-615.1, -123.6], [-643.4, -133.5], [-671.2, -144.7], [-697.6, -158.8], [-722.5, -175.5],
      [-745.2, -195.1], [-765.3, -217.4], [-782.8, -241.7], [-797.3, -267.9], [-809.6, -295.3],
      [-821.5, -322.8], [-833.5, -350.3], [-845.6, -377.8], [-857.9, -405.1], [-878.2, -426.8],
      [-906.2, -436.5], [-936.1, -437], [-965.8, -432.4], [-994.2, -423.2], [-1011.3, -413],
      [-1026.3, -387.4], [-1028.6, -363.7], [-1020.3, -335.1], [-1003, -310.7], [-981.9, -289.5],
      [-958.9, -270.2], [-935.3, -251.7], [-911.3, -233.8], [-886.5, -216.8], [-861.4, -200.5],
      [-835.9, -184.6], [-810.1, -169.4], [-783.6, -155.2], [-756.9, -141.6], [-729.9, -128.5],
      [-702.4, -116.5], [-674.7, -104.9], [-646.5, -94.9], [-618.2, -85], [-589.9, -75],
      [-561.6, -65], [-533.3, -55.1], [-505, -45.1], [-476.7, -35.1], [-448.4, -25.2],
      [-420.1, -15.2], [-391.8, -5.3], [-363.5, 4.6], [-335.2, 14.5], [-306.9, 24.5],
      [-278.5, 34.4], [-250.2, 44.3], [-222, 54.4], [-193, 61.6], [-163.3, 58.8],
      [-134.6, 50.1], [-106.6, 39.4], [-79.6, 26.5], [-54.2, 10.4], [-30.1, -7.4],
      [-7.5, -27.1], [14.5, -47.5], [36.5, -67.9], [58.6, -88.2], [80.7, -108.4],
      [102.8, -128.7], [126.2, -147.5], [146.1, -160.8], [155.7, -163.6], [180.1, -147.7],
      [192.7, -137.8], [202.4, -135.8], [227.3, -151], [251, -169.3], [278.4, -181.4],
      [308.2, -184.3], [338, -181.3], [367, -173.8], [393.9, -160.7], [419, -144.3],
      [441.9, -125], [462.8, -103.5], [482.6, -81], [502, -58.1], [521.2, -35],
      [540.3, -12], [559.5, 11.1], [578.7, 34.2], [597.8, 57.3], [617, 80.4],
      [636.1, 103.5], [655.3, 126.5], [674.5, 149.6], [693.6, 172.7], [712.8, 195.8],
      [731.9, 218.9], [750.9, 242.1], [770, 265.3], [789, 288.4], [808.1, 311.6],
      [827.1, 334.8], [846.2, 358], [865.2, 381.1], [884.3, 404.3], [903.3, 427.5],
      [921.2, 451.5], [932.6, 479.1], [934, 508.9], [926.7, 537.9], [917.1, 566.4],
      [906.4, 585.5], [883.6, 601], [854.1, 604.1], [833.4, 597.2], [811.6, 577.1],
      [795.8, 551.6], [779.6, 526.4], [763.3, 501.2], [747, 476], [730.4, 451],
      [710.9, 428.4], [684.6, 414.6], [654.8, 411.4], [625, 409.5], [597.7, 397.7],
      [577.4, 375.9], [566.3, 348.1], [558.6, 319.1], [550.6, 290.2], [536.1, 264.2],
      [513.2, 245.2], [484.9, 235.8], [454.9, 234.4], [424.9, 233.1], [396.7, 223.5],
      [373, 205.4], [357, 182.7], [350.4, 157.7], [354.1, 128.2], [364.7, 100.2],
      [375.3, 72.1], [381.9, 43], [378.5, 15.4], [364.9, -11.1], [342.5, -30.7],
      [316, -44.9], [288.5, -56.8], [259.9, -65.5], [230.2, -69.7], [200.2, -69.6],
      [170.4, -66.5], [142.2, -56.4], [114.9, -44.2], [88.8, -29.3], [65.7, -10.3],
      [44.5, 10.9], [25.3, 34], [6.1, 57], [-13.1, 80.1], [-32.3, 103.1],
      [-50.4, 124.4],
    ],
  },
  {
    id: 'interlagos',
    name: 'Interlagos',
    fullName: 'Autódromo José Carlos Pace',
    country: 'Brazil',
    realLength: 4295,
    length: 1480,
    blurb:
      'Anticlockwise, unlike everything else on this list: the Senna S drops away from the line immediately, and the lap climbs back up through Junção to the pit straight.',
    elevation: [[-0.01, 0], [0.05, -10], [0.11, 0], [0.2, 0], [0.5, 18], [0.8, 0], [0.9, 0], [0.95, -8], [1.0, 0]],
    verticalScale: 0.75,
    runoff: [[0.08, 0.12, 'runoffAsphalt'], [0.35, 0.4, 'gravel'], [0.6, 0.65, 'gravel']],
    // White, green and yellow — the Brazilian national colours by way of
    // Petrobras, whose own livery the kerbing follows.
    kerbs: ['#e9e9ec', '#009c3b', '#ffdf00'],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 32, scale: 1 },
    ],
    points: [
      [-236.1, 168.1], [-229, 195.2], [-222, 222.3], [-214.9, 249.4], [-207.9, 276.5],
      [-200.8, 303.6], [-193.8, 330.7], [-186.7, 357.8], [-179.7, 384.9], [-172.7, 412],
      [-165.6, 439.1], [-158.6, 466.2], [-151.6, 493.3], [-144.5, 520.4], [-137.5, 547.5],
      [-130.5, 574.6], [-120.3, 600.7], [-103.1, 625], [-88.5, 635.4], [-72.9, 637.5],
      [-48.1, 622], [-28.9, 601.6], [-15.3, 589.9], [0.1, 587], [27, 599.6],
      [52.9, 614.6], [81.6, 623], [109.6, 624.6], [137.4, 621.6], [164, 613.3],
      [190.1, 598.6], [211.5, 580.6], [229.2, 559], [243.1, 532.5], [252, 505.9],
      [260.1, 479.1], [268, 452.3], [276, 425.4], [283.8, 398.5], [291.7, 371.7],
      [299.3, 344.7], [306.7, 317.7], [313.4, 290.5], [319.9, 263.3], [326.3, 236],
      [333.5, 209], [340.8, 181.9], [348, 154.9], [355.3, 127.8], [362.5, 100.8],
      [369.9, 73.7], [377.3, 46.7], [384.7, 19.7], [392.3, -7.2], [400, -34.1],
      [407.7, -61.1], [412.2, -88.6], [406.5, -109.7], [391.1, -124.9], [362.4, -133.4],
      [334.9, -138.3], [307.2, -142.9], [279.5, -146.5], [251.6, -144.5], [225.1, -135.8],
      [199.9, -119.7], [179.9, -100.1], [162.4, -78.3], [145.7, -55.8], [129.2, -33.2],
      [112.8, -10.5], [96.4, 12.2], [80, 34.9], [63.5, 57.5], [46.9, 80.1],
      [30.4, 102.7], [13.8, 125.3], [-2.8, 147.8], [-20, 169.9], [-40.7, 185.3],
      [-69.7, 192.2], [-97.6, 190.9], [-124.5, 183.5], [-150.7, 169.2], [-167.3, 152.1],
      [-177.3, 124], [-182.8, 96.5], [-187.3, 68.9], [-191.8, 41.2], [-193.2, 13.3],
      [-191, -0.4], [-178.7, -13.1], [-163.4, -16.8], [-136.4, -4.7], [-113.3, 11],
      [-93.9, 15.1], [-71.2, 8.3], [-55.2, -9.2], [-50.6, -32.4], [-63.2, -58.9],
      [-83, -78.7], [-103, -98.4], [-122.1, -118.8], [-137.6, -142], [-146.8, -168.4],
      [-153.6, -195.6], [-157.3, -221.2], [-154.7, -232.8], [-141.3, -244.4], [-129.7, -246.7],
      [-105.5, -230.2], [-86.5, -209.6], [-67.5, -189], [-48.3, -168.6], [-25.4, -152.7],
      [2.9, -143.2], [30.9, -141.7], [58.1, -147.5], [82.6, -160.8], [103.5, -182.1],
      [119.1, -205.5], [134.1, -229.1], [149, -252.8], [163.8, -276.6], [178.6, -300.3],
      [192.9, -324.4], [199.5, -338.9], [199.1, -350.8], [185.4, -367.4], [160.1, -379.3],
      [133.9, -389.2], [107.6, -398.6], [82.7, -406.1], [52.9, -404.1], [25.3, -399.4],
      [-2.2, -394.3], [-29.1, -386.5], [-55, -375.8], [-80.4, -364], [-105.4, -351.5],
      [-129.1, -336.7], [-150.6, -318.7], [-170.1, -296], [-183.5, -271.5], [-192.5, -245],
      [-199.5, -217.8], [-206.3, -190.7], [-212.8, -163.4], [-219.3, -136.2], [-225.8, -109],
      [-232.4, -81.7], [-239, -54.5], [-245.6, -27.3], [-251.2, 0.1], [-254.4, 27.9],
      [-255.2, 55.9], [-253, 83.8], [-248.8, 111.5], [-243.2, 138.9],
    ],
  },
  {
    id: 'nurburgring',
    name: 'Nürburgring',
    fullName: 'Nürburgring Grand Prix Strecke',
    country: 'Germany',
    realLength: 5103,
    length: 1711,
    blurb:
      'The modern Grand Prix circuit built alongside the old Nordschleife, not the 20 km Nordschleife itself — a tighter, stop-start lap through the Mercedes Arena complex.',
    elevation: [[0.02, 0], [0.1, -8], [0.18, 0], [0.15, 0], [0.35, 20], [0.55, 0], [0.5, 0], [0.7, -14], [0.9, 0]],
    verticalScale: 0.8,
    runoff: [[0.02, 0.06, 'runoffAsphalt'], [0.28, 0.33, 'gravel'], [0.85, 0.9, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [-65.6, 172.2], [-74.7, 200.8], [-83.8, 229.4], [-91.8, 258.2], [-89.8, 287.9],
      [-74.8, 313.6], [-50.5, 330.7], [-23.4, 343.7], [3.6, 356.7], [27.1, 371.6],
      [35.3, 385], [35.9, 404.8], [21.6, 428.3], [-3.8, 444.1], [-30.2, 458.4],
      [-56.5, 472.7], [-82.7, 487.3], [-108.2, 503.1], [-133.2, 519.8], [-157, 538],
      [-180.2, 556.9], [-202.8, 576.7], [-224.2, 597.7], [-244.6, 619.6], [-263.4, 643.1],
      [-281.7, 666.8], [-299.2, 691.2], [-314.8, 716.7], [-329.6, 742.8], [-345.7, 768.1],
      [-356.1, 777.3], [-373.3, 782], [-396.2, 775.7], [-406.7, 766.7], [-415.1, 740.6],
      [-410.4, 721.4], [-390.2, 699.4], [-368.8, 678.4], [-347.6, 657.2], [-326.3, 636],
      [-305, 614.9], [-283.8, 593.7], [-262.5, 572.6], [-241.2, 551.4], [-220, 530.3],
      [-202.4, 511.2], [-190.7, 483.7], [-186.5, 454.2], [-190.3, 424.5], [-199.4, 396],
      [-204.7, 366.5], [-201.7, 336.8], [-193.4, 308], [-184.3, 279.4], [-175, 250.8],
      [-165.4, 222.4], [-155.9, 194], [-146.3, 165.6], [-136.9, 137.1], [-128.1, 108.4],
      [-120, 79.5], [-111.2, 50.9], [-102, 22.3], [-93, -6.3], [-85.3, -33.2],
      [-87, -52.8], [-98.7, -68.8], [-125.5, -82.1], [-153, -94.1], [-180.5, -106.1],
      [-208, -118], [-233.3, -129.9], [-249.6, -147.1], [-257.4, -175.8], [-256.8, -197.7],
      [-242.6, -223.9], [-223.4, -246.9], [-204.2, -270], [-184.9, -293], [-165.6, -315.9],
      [-146.2, -338.8], [-126.9, -361.8], [-107.9, -385], [-88.8, -408.1], [-69.8, -431.3],
      [-50.7, -454.4], [-31.6, -477.6], [-10.3, -498.7], [15, -514.5], [43, -525.1],
      [72.1, -532.7], [101.1, -540.3], [130.1, -547.9], [159.1, -555.6], [188.1, -563.2],
      [217.1, -570.8], [246.1, -578.4], [275.1, -586.1], [304.1, -593.7], [333.2, -601.3],
      [362.2, -608.9], [391.2, -616.5], [420.2, -624.2], [445.3, -630.8], [458.9, -638.7],
      [471.5, -665.7], [481.3, -685.2], [505.4, -702.7], [533.8, -712], [562.9, -719.2],
      [592.2, -725.8], [621.4, -732.5], [650.7, -739.1], [668.5, -741.3], [689.1, -733.9],
      [704.9, -718.8], [711.5, -690.2], [706.3, -667], [687.5, -643.8], [666.6, -622.3],
      [645.6, -600.8], [624.7, -579.4], [603.7, -558], [582.7, -536.5], [561.7, -515.1],
      [540.6, -493.7], [519.5, -472.5], [498.3, -451.2], [477.2, -429.9], [456, -408.7],
      [434.9, -387.4], [413.8, -366.1], [392.6, -344.8], [371.5, -323.5], [350.4, -302.2],
      [329.3, -280.9], [308.3, -259.5], [287.3, -238.1], [266.3, -216.7], [245.2, -195.3],
      [223.3, -174.9], [200.6, -155.2], [177, -136.7], [152.9, -118.9], [127.9, -102.4],
      [102.5, -86.3], [90.4, -79.4], [82.7, -77.9], [75.8, -81.6], [70.3, -92.1],
      [75, -121.5], [85.3, -149.6], [99.3, -176.1], [114, -202.2], [120.5, -231.1],
      [113.3, -257.9], [94.9, -278.5], [67, -288.2], [37.1, -286.5], [7.5, -282],
      [-21.8, -275.8], [-50.6, -267.2], [-76, -255.7], [-92.7, -238.7], [-98, -225.8],
      [-94.5, -206.5], [-79.1, -191.4], [-50.1, -187.5], [-20.2, -187.2], [-7.1, -182.7],
      [-0.1, -173.3], [2.2, -143.6], [-1.4, -113.8], [-5, -84], [-9.2, -54.3],
      [-14.5, -24.8], [-19.7, 4.7], [-25, 34.2], [-31.1, 63.6], [-40.1, 92.2],
      [-49.3, 120.8], [-58.5, 149.3],
    ],
  },
  {
    id: 'imola',
    name: 'Imola',
    fullName: 'Autodromo Enzo e Dino Ferrari',
    country: 'Italy',
    realLength: 4900,
    length: 1100,
    blurb:
      'Tamburello and Villeneuve run flat-out into the tightening Tosa hairpin; Rivazza\'s late-apex double right is the last chance to make a run at the pit straight.',
    elevation: [[0.02, 0], [0.08, -8], [0.14, 0], [0.2, 0], [0.35, 14], [0.5, 0], [0.45, 0], [0.55, -10], [0.65, 0], [0.65, 0], [0.75, 9], [0.85, 0]],
    verticalScale: 0.8,
    runoff: [[0.36, 0.4, 'gravel'], [0.55, 0.6, 'gravel'], [0.78, 0.82, 'gravel']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [-66.5, -371.8], [-96.3, -369.4], [-126.2, -366.9], [-156.1, -364.3], [-186, -361.5],
      [-215.9, -358.8], [-245.3, -353.3], [-274.5, -346.3], [-303.6, -339.3], [-332.8, -332.2],
      [-344.9, -325.6], [-355.2, -298], [-361.6, -272.9], [-382.3, -252.1], [-409.4, -239.2],
      [-436.7, -226.7], [-463.8, -214], [-484.7, -198.7], [-498.1, -172.1], [-509.5, -144.3],
      [-520.8, -116.5], [-532.1, -88.7], [-543.4, -60.9], [-554.7, -33.1], [-566, -5.4],
      [-577.3, 22.4], [-588.6, 50.2], [-599.9, 78], [-611.2, 105.8], [-622.5, 133.6],
      [-633.8, 161.4], [-645, 189.2], [-644, 218.6], [-636.3, 247.6], [-630.6, 268.8],
      [-631.7, 286.6], [-651.6, 308.6], [-674.7, 327.7], [-697.8, 346.7], [-720.7, 366.1],
      [-743.6, 385.6], [-766.5, 405], [-789.4, 424.3], [-811.9, 444.1], [-826.9, 460.2],
      [-832.1, 473.1], [-828.3, 494.4], [-817.2, 508.3], [-800.6, 514.7], [-770.8, 512.8],
      [-741.1, 508.7], [-711.3, 504.6], [-681.6, 500.4], [-651.9, 496.3], [-622.2, 492.1],
      [-592.5, 487.7], [-562.8, 483.5], [-533.1, 479.7], [-503.3, 476], [-473.5, 472.9],
      [-443.5, 472], [-413.6, 473.4], [-383.8, 477.3], [-354.3, 482.5], [-324.8, 487.8],
      [-295.3, 493.1], [-265.7, 498.3], [-239.9, 499.8], [-216.7, 488.7], [-197.7, 465.8],
      [-184.2, 439], [-171.1, 412.1], [-161, 383.9], [-155.1, 354.5], [-154.5, 324.5],
      [-158.7, 294.9], [-165.5, 265.6], [-172, 236.4], [-178.5, 207.1], [-184.9, 177.8],
      [-191.4, 148.5], [-195.3, 124.9], [-183.8, 97.7], [-167.7, 72.4], [-151.5, 47.2],
      [-141.8, 32], [-134.3, 25.6], [-105.7, 30.8], [-78, 42.2], [-48.2, 44.6],
      [-18.3, 44.4], [11.7, 44.2], [41.7, 43.9], [71.7, 43.7], [101.7, 43.5],
      [131.7, 43.3], [161.7, 43], [191.7, 42.8], [221.7, 42.6], [251.7, 42.4],
      [281.7, 42.1], [311.7, 41.9], [341.7, 41.7], [361.7, 42.1], [369.2, 44.4],
      [376.9, 62.5], [381.1, 69.1], [388.3, 72], [416.5, 62.5], [444.2, 51.2],
      [472, 39.9], [499.8, 28.6], [527.6, 17.2], [555.4, 5.9], [582.1, -7.7],
      [607.4, -23.7], [631.7, -41.3], [655.9, -59.1], [679, -78.2], [701, -98.6],
      [722.4, -119.6], [743.7, -140.7], [765, -161.8], [786.4, -182.9], [807.7, -204],
      [829.6, -224.4], [854, -241.9], [880.7, -255.4], [908.2, -267.5], [935.7, -279.5],
      [963.3, -291.1], [991, -302.7], [1002.9, -310], [1009.6, -322.1], [1003.1, -350.8],
      [991.1, -378.2], [979.1, -405.7], [968.9, -422.8], [951.8, -432.7], [922.6, -429.1],
      [894.5, -418.8], [866.4, -408.4], [838.5, -397.4], [810.6, -386.2], [782.8, -375.1],
      [754.9, -363.9], [727.1, -352.7], [699.3, -341.5], [670.9, -331.7], [641.8, -324.5],
      [612.2, -320], [582.2, -319.4], [552.2, -320.1], [522.3, -320.8], [492.3, -321.5],
      [462.3, -322.2], [432.3, -322.6], [402.3, -322.8], [372.3, -322.9], [342.3, -323.1],
      [312.3, -323.5], [282.8, -328.1], [253.4, -334.3], [224.1, -340.6], [194.7, -346.8],
      [165.4, -353], [136, -359.3], [106.7, -365.5], [77.1, -370.5], [47.3, -373.7],
      [17.4, -375.5], [-12.6, -375.8], [-42.5, -373.9],
    ],
  },
  {
    id: 'hockenheim',
    name: 'Hockenheim',
    fullName: 'Hockenheimring Baden-Württemberg',
    country: 'Germany',
    realLength: 4557,
    length: 2676,
    blurb:
      'A sprint down to the Mercedes-Arena hairpin, then the stop-start Motodrom stadium section, where the outward and inward halves of the lap run close enough to need the extra room this trace gives them.',
    elevation: [[0.25, 0], [0.4, 2], [0.55, 0]],
    runoff: [[0.5, 0.55, 'runoffAsphalt'], [0.75, 0.8, 'runoffAsphalt']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [-528.6, -136.3], [-511.5, -161], [-492, -183.7], [-471.7, -205.8], [-450.8, -227.3],
      [-430.2, -249.1], [-409.1, -270.5], [-388.1, -291.9], [-367.1, -313.3], [-346.1, -334.7],
      [-325.2, -356.2], [-304.2, -377.6], [-284, -399.8], [-263.2, -421.4], [-244.5, -439.4],
      [-236.1, -444.7], [-224.4, -444.8], [-204.1, -423.5], [-194, -399.7], [-192.2, -369.8],
      [-190.4, -343.9], [-175.8, -318.2], [-152.3, -299.6], [-126.5, -284.4], [-100.5, -269.4],
      [-74.1, -255.2], [-47.4, -241.6], [-20.6, -228.1], [6.3, -214.8], [34, -203.3],
      [62.1, -192.8], [90.5, -183.1], [119.2, -174.6], [148.4, -167.5], [177.7, -161.2],
      [207.3, -156.4], [237.1, -152.9], [267, -150.5], [296.9, -148.9], [326.9, -148.4],
      [356.9, -149.5], [386.7, -152.4], [416.4, -156.9], [445.7, -163], [474.7, -170.8],
      [503.3, -179.7], [531.7, -189.4], [559.9, -199.7], [588, -210.1], [616.3, -220.1],
      [644.4, -230.5], [672.6, -240.9], [700.7, -251.3], [728.8, -261.6], [757, -272.1],
      [785, -282.7], [813.1, -293.1], [841.3, -303.5], [856.2, -309.2], [866.1, -309.1],
      [871.2, -306.1], [874.1, -298.8], [872.9, -291], [849.2, -273], [824.3, -256.3],
      [798.9, -240.4], [773.4, -224.6], [748.4, -208], [723.6, -191.2], [698.8, -174.3],
      [674.1, -157.3], [648.9, -141], [623.7, -124.8], [598.5, -108.5], [573.4, -92],
      [548.4, -75.5], [525, -60.3], [495.5, -57.2], [465.7, -60.5], [435.9, -64.4],
      [406.2, -68.4], [376.5, -72.5], [346.7, -76.5], [317, -80.5], [287.3, -84.8],
      [257.6, -88.6], [243.7, -90], [236.4, -87], [228.4, -59.2], [229.5, -29.3],
      [234.8, 0.2], [248.1, 26.8], [263.7, 47.6], [269.4, 66.6], [263.5, 95.7],
      [247.6, 121], [225.3, 140.8], [200.4, 157.6], [175.3, 174.1], [150.2, 190.5],
      [125.1, 206.9], [100.1, 223.4], [75, 239.8], [49.8, 256.2], [24.7, 272.6],
      [-0.4, 288.9], [-25.9, 304.8], [-51.9, 319.6], [-80.8, 326.3], [-110.6, 322.7],
      [-137.1, 309.1], [-157.7, 287.4], [-177.1, 264.5], [-196.3, 241.5], [-215.6, 218.4],
      [-235.1, 195.7], [-254.8, 173], [-274.4, 150.4], [-294.6, 128.2], [-309.3, 118],
      [-332.8, 115.8], [-347.6, 121.4], [-360.4, 136.4], [-363.8, 163.8], [-355.5, 192.6],
      [-346.4, 221.2], [-335.1, 248.8], [-313.4, 268.8], [-286.2, 281.4], [-261, 297.2],
      [-242.4, 320.7], [-225.2, 345.4], [-210.2, 371.2], [-209.5, 390.9], [-217.6, 406.8],
      [-241.2, 425.2], [-265.4, 442.9], [-288.8, 458.2], [-311.9, 463.8], [-336.9, 457.6],
      [-359.3, 437.9], [-376.3, 413.3], [-389.8, 386.4], [-403.1, 359.6], [-416.4, 332.7],
      [-429.8, 305.8], [-442.4, 278.7], [-455.1, 251.4], [-468, 224.4], [-481, 197.4],
      [-493.9, 170.3], [-506.9, 143.2], [-520.3, 116.4], [-534.2, 89.9], [-548.2, 63.3],
      [-562.2, 36.8], [-576.5, 10.4], [-584.7, -5.6], [-578.2, -33.7], [-565.4, -60.9],
      [-552.9, -88.1], [-540, -115.2],
    ],
  },
  {
    id: 'paulricard',
    name: 'Paul Ricard',
    fullName: 'Circuit Paul Ricard',
    country: 'France',
    realLength: 5757,
    length: 1457,
    blurb:
      'The Mistral straight down to Signes, taken flat, then the tightening Double Droite de Beausset drags the lap back through the infield to the line.',
    elevation: [[0.1, 0], [0.3, 4], [0.5, 0]],
    runoffDefault: 'runoffAsphalt',
    wallMargin: 18,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [474.8, 241.5], [453.7, 220.3], [427.9, 205.3], [398.3, 202.1], [369.7, 210.1],
      [346.8, 226], [334.7, 244.3], [331.7, 273.9], [331.3, 303.9], [330.2, 327.9],
      [327, 337.2], [320.5, 341.6], [294.1, 329.5], [270.1, 311.5], [246, 293.7],
      [222, 275.6], [198.2, 257.4], [174.4, 239.2], [150.6, 220.9], [126.8, 202.7],
      [103, 184.4], [79.2, 166.2], [55.3, 148], [31.3, 130], [7.3, 112],
      [-16.7, 94], [-40.7, 76], [-64.6, 57.9], [-88.6, 39.9], [-112.6, 21.9],
      [-136.6, 3.9], [-160.6, -14.1], [-184.6, -32.1], [-208.6, -50.1], [-232.5, -68.2],
      [-256.4, -86.3], [-280.4, -104.4], [-304.3, -122.5], [-328.2, -140.6], [-352.1, -158.8],
      [-375.9, -176.9], [-399.9, -195], [-424.4, -212.2], [-440.7, -219.5], [-458.5, -218.9],
      [-483.3, -202.2], [-507.3, -184.3], [-532.3, -172.2], [-556.2, -171.6], [-583.6, -183.4],
      [-610.2, -197.2], [-636.5, -211.6], [-659.2, -231.1], [-680.2, -252.6], [-703.2, -271.7],
      [-727.1, -289.9], [-751, -308], [-775.3, -325.6], [-800.4, -342], [-826.5, -356.7],
      [-854, -368.7], [-882, -379.3], [-909.5, -391.4], [-936, -405.4], [-962.1, -420.2],
      [-987.9, -435.6], [-1009.4, -450.2], [-1018.5, -463.1], [-1019.1, -492.6], [-1016.4, -514.4],
      [-1021.2, -533.6], [-1039.3, -551.9], [-1067.9, -559.3], [-1097.7, -556.3], [-1127.6, -553],
      [-1137.5, -553.1], [-1144.7, -556.3], [-1147.7, -561.4], [-1139.6, -589.9], [-1129.8, -618.3],
      [-1120.3, -644.6], [-1104.9, -662.4], [-1078.5, -670.6], [-1049.9, -663], [-1024.5, -647.2],
      [-1002.1, -627.3], [-983.8, -603.6], [-969.2, -577.5], [-957.6, -549.8], [-945.2, -522.5],
      [-924.9, -501.1], [-897.5, -489], [-869.4, -478.5], [-841.3, -468.1], [-813.2, -457.6],
      [-785.1, -447.1], [-757, -436.6], [-728.9, -426.1], [-700.8, -415.6], [-672.7, -405.2],
      [-644.5, -394.8], [-616.4, -384.4], [-588.2, -374], [-560.1, -363.6], [-532, -353.2],
      [-503.8, -342.8], [-475.7, -332.4], [-447.6, -321.9], [-419.4, -311.5], [-391.3, -301.1],
      [-363.2, -290.7], [-335, -280.3], [-306.9, -269.9], [-278.8, -259.4], [-250.7, -248.9],
      [-222.6, -238.5], [-194.5, -228], [-166.4, -217.5], [-138.3, -207], [-110.2, -196.5],
      [-82.1, -186], [-54, -175.5], [-25.8, -165.2], [2.4, -154.9], [30.5, -144.5],
      [58.6, -134], [86.8, -123.6], [114.9, -113.2], [143, -102.8], [171.2, -92.4],
      [199.3, -82], [227.5, -71.6], [255.6, -61.2], [283.7, -50.8], [311.9, -40.4],
      [340, -30], [368.1, -19.5], [396.2, -9], [424.3, 1.4], [452.5, 11.9],
      [480.6, 22.3], [508.7, 32.8], [536.8, 43.3], [564.9, 53.7], [593, 64.2],
      [621.1, 74.6], [649.3, 85.1], [677.4, 95.6], [705.5, 106], [733.6, 116.5],
      [761.7, 127], [789.8, 137.4], [817.9, 147.9], [845.7, 159.1], [870.1, 176.4],
      [890.9, 198], [908.6, 222.1], [918.9, 250.2], [925.4, 279.5], [927.4, 309.4],
      [927.6, 339.4], [927.8, 369.4], [927.6, 399.4], [927.4, 429.4], [927.1, 459.4],
      [926.9, 489.4], [926.5, 519.4], [921.5, 546.9], [907.3, 573.2], [886.7, 594.8],
      [860.2, 608.4], [830.7, 613.4], [801, 609.8], [774.2, 596.6], [752.1, 576.6],
      [741.3, 559.8], [740.9, 530.4], [752.3, 502.7], [767.3, 476.7], [784.2, 452],
      [802.2, 428], [820.2, 404], [838.3, 380.1], [854, 354.6], [860.1, 333.5],
      [858.1, 303.7], [844.2, 277.5], [823.9, 265.4], [794.7, 268.6], [768.5, 283.3],
      [741.6, 296.5], [713.2, 306.1], [683.8, 312], [654, 314.7], [624, 314.3],
      [594.3, 310.1], [565.4, 302.1], [538, 290.1], [512.5, 274.4], [489.5, 255.1],
    ],
  },
  {
    id: 'catalunya',
    name: 'Catalunya',
    fullName: 'Circuit de Barcelona-Catalunya',
    country: 'Spain',
    realLength: 4667,
    length: 1100,
    blurb:
      'A fast, wide-open first sector through Turns 2 and 3 gives way to a slow, technical final third; the closing chicane is the last chance to change the run down the pit straight.',
    elevation: [[0.1, 0], [0.25, 8], [0.4, 0], [0.75, 0], [0.85, -6], [0.95, 0]],
    runoff: [[0.35, 0.4, 'gravel'], [0.9, 0.97, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [238.1, -552.8], [262, -534.7], [286, -516.6], [309.7, -498.3], [324.1, -487.4],
      [331.5, -485], [353.9, -503.9], [359.7, -509.3], [367.1, -511.7], [392.8, -496.5],
      [418.2, -480.5], [443.2, -464], [463.1, -441.8], [473.7, -413.9], [475.2, -384],
      [467.2, -355.3], [451.8, -329.6], [435.6, -304.3], [419.5, -279], [403.4, -253.7],
      [387.3, -228.4], [371.1, -203.1], [355, -177.8], [338.9, -152.5], [322.8, -127.2],
      [306.7, -101.9], [290.7, -76.6], [274.6, -51.2], [258.6, -25.9], [242.6, -0.5],
      [226.6, 24.9], [210.6, 50.3], [194.4, 75.5], [178.1, 100.7], [162, 126],
      [146.1, 151.4], [130.1, 176.9], [114.2, 202.3], [98.3, 227.7], [82.3, 253.1],
      [66.4, 278.5], [50.3, 303.8], [34.2, 329.1], [18.1, 354.4], [2, 379.7],
      [-14.1, 405.1], [-30.2, 430.4], [-46.3, 455.7], [-62, 481.2], [-77.9, 506.7],
      [-94, 532], [-109.7, 547.1], [-128.8, 551.4], [-149.9, 546.2], [-175.2, 530.2],
      [-201.7, 522.1], [-223.4, 524], [-249.2, 538.9], [-273.4, 556.6], [-297.7, 574.2],
      [-322.9, 590.2], [-351.6, 598.7], [-381.5, 599.2], [-410.5, 592.1], [-436.2, 576.8],
      [-457.1, 555.4], [-472.4, 529.8], [-481.9, 501.4], [-484.9, 471.6], [-483, 441.7],
      [-476.9, 412.3], [-467.9, 383.7], [-455.2, 356.6], [-439.3, 331.2], [-423.1, 306],
      [-407.1, 280.6], [-391.1, 255.2], [-375.2, 229.8], [-359.2, 204.4], [-343.2, 179],
      [-327.1, 153.7], [-309.9, 137.4], [-289.2, 130.5], [-267.6, 132.4], [-243.4, 146],
      [-224.7, 169.3], [-216.3, 197.8], [-215.9, 227.7], [-223.4, 256.7], [-237.1, 283.3],
      [-253.6, 308.3], [-269.8, 333.6], [-285.9, 358.9], [-301.9, 384.2], [-318, 409.6],
      [-329.7, 430.4], [-331, 448.1], [-323.5, 464.1], [-309, 474.2], [-291.3, 475.8],
      [-263.5, 465], [-236.1, 452.7], [-208.8, 440.4], [-181.4, 428.2], [-154, 415.9],
      [-128.2, 400.8], [-104.4, 382.6], [-83.4, 361.2], [-65.8, 336.9], [-49.6, 311.7],
      [-33.5, 286.4], [-17.9, 260.8], [-14.1, 245.4], [-18.5, 226.1], [-40.4, 206.2],
      [-65.3, 189.6], [-85.1, 167.4], [-97.8, 140.3], [-109.5, 112.6], [-121.2, 85],
      [-133, 57.4], [-144.9, 29.9], [-156.7, 2.3], [-165.4, -26.3], [-164, -56.1],
      [-153, -83.8], [-133.3, -106.2], [-107.9, -122.2], [-81.2, -135.8], [-54.4, -149.3],
      [-27.6, -162.7], [-0.8, -176.3], [25.9, -189.9], [52.7, -203.5], [79.4, -217.1],
      [106.2, -230.6], [133, -244.1], [159.8, -257.5], [186.6, -271], [213.2, -284.8],
      [239.9, -298.6], [266.5, -312.4], [293.1, -326.2], [319.6, -340.3], [331.3, -351],
      [335.9, -363.9], [332.9, -381.3], [311.8, -402], [284.1, -413.1], [254.3, -415.8],
      [225, -410.3], [198.6, -396.1], [175.6, -376.9], [151.8, -362.6], [126.2, -359.5],
      [103.8, -367.2], [83.2, -388.5], [76, -415.3], [82.7, -444.1], [103.1, -465.7],
      [126.4, -484.5], [149.7, -503.4], [172.8, -522.5], [195.9, -541.8], [211.7, -553.8],
      [225.1, -557.3],
    ],
  },
  {
    id: 'zandvoort',
    name: 'Zandvoort',
    fullName: 'Circuit Zandvoort',
    country: 'Netherlands',
    realLength: 4244,
    length: 1542,
    blurb:
      'Banked Turn 3 and the banked final Turn 14 bookend a lap threaded between the dunes — narrow, blind in places, and unforgiving of a wide exit.',
    elevation: [[0.05, 0], [0.2, 5], [0.35, 0], [0.55, 0], [0.7, -4], [0.85, 0]],
    banking: [[0.12, 0], [0.15, 0.19], [0.18, 0], [0.93, 0], [0.96, 0.19], [0.99, 0]],
    // Orange and white since the 2020 rebuild — Dutch orange throughout, not
    // the red the circuit used to share with everyone else.
    kerbs: ['#ff6a13', '#e9e9ec'],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
      { kind: 'monument', name: 'National motorsport monument', at: 0.04, offset: -26, scale: 1.2 },
    ],
    runoffDefault: 'gravel',
    wallMargin: 7,
    points: [
      [-206.1, -483.6], [-190.1, -501], [-169.2, -506.8], [-151.8, -502.7], [-134.6, -486.4],
      [-128.7, -463.5], [-137.4, -435], [-147.8, -409], [-158.7, -383.2], [-170.2, -357.7],
      [-179.8, -331.4], [-186.6, -304.3], [-189.6, -276.4], [-189.8, -248.4], [-190, -222.4],
      [-202.7, -198.1], [-228.2, -182.8], [-254.4, -172.8], [-280.5, -162.8], [-295.2, -152.8],
      [-302.4, -136.5], [-299.6, -117], [-288.4, -103.1], [-274, -96.6], [-244.7, -101.2],
      [-218.1, -109.8], [-191.5, -118.6], [-164.6, -126.4], [-137.3, -132.8], [-109.5, -136.1],
      [-81.5, -136.4], [-53.6, -134.2], [-26.1, -129.3], [1.2, -123.1], [28.5, -116.8],
      [56.2, -113], [84.2, -113.3], [111.5, -119.1], [137.5, -129.7], [161.9, -143.3],
      [185.5, -158.3], [209.9, -172.2], [235.6, -183], [263, -188.7], [290.9, -190.1],
      [318.9, -188.9], [346.9, -187.3], [374.8, -185.7], [402.8, -183.7], [430.2, -178.6],
      [455.6, -166.9], [476.7, -148.7], [492.8, -123.5], [500.3, -96.6], [500.1, -68.7],
      [492.1, -42], [477.7, -18], [461.6, 5], [445.6, 27.9], [429.8, 51],
      [415.9, 75.3], [404, 100.6], [391.5, 125.7], [379, 150.8], [370.5, 161.7],
      [341.9, 169.3], [314, 168.8], [286.3, 164.6], [259.4, 156.9], [233.8, 145.7],
      [209.8, 131.2], [188.1, 113.5], [175.9, 100.4], [171.5, 87.3], [175.1, 72],
      [196.9, 51.7], [222, 39.4], [249.5, 34.6], [277.2, 30.4], [304.4, 23.7],
      [330.7, 14.1], [352, 3.3], [366.7, -15.4], [370.7, -38.8], [361.3, -62.7],
      [344.8, -76.9], [315.7, -83.3], [287.8, -86.1], [260, -88.6], [232, -89.6],
      [204, -89.3], [176, -87.9], [148.1, -85.3], [120.4, -81.7], [92.7, -77.3],
      [65.2, -71.8], [38, -65.4], [11, -57.8], [-15.6, -49.2], [-42, -39.8],
      [-68, -29.5], [-93.9, -18.8], [-119.4, -7.2], [-144, 6.2], [-157.9, 14.1],
      [-167.5, 16.1], [-174.8, 13.3], [-189.8, -10.3], [-203.4, -21.7], [-221, -24.6],
      [-241.1, -16.4], [-251.8, -2.2], [-251.3, 27.3], [-246.6, 54.9], [-241.8, 82.5],
      [-237.1, 110.1], [-232.4, 137.7], [-227.7, 165.3], [-222.9, 192.9], [-218.2, 220.5],
      [-213.5, 248.1], [-213.4, 275.8], [-226.7, 300.1], [-252.2, 315.1], [-280, 317.3],
      [-308, 317.9], [-336, 318.5], [-363.9, 316.4], [-390.6, 308.4], [-415.2, 295],
      [-436.6, 277], [-453.6, 254.9], [-465.2, 229.4], [-471.6, 202.2], [-471.9, 174.3],
      [-466.5, 146.9], [-456.2, 120.8], [-445.4, 95], [-434.7, 69.1], [-423.9, 43.3],
      [-413.2, 17.4], [-402.5, -8.5], [-391.8, -34.4], [-381.1, -60.2], [-370.4, -86.1],
      [-359.7, -112], [-349, -137.9], [-338.3, -163.8], [-327.6, -189.6], [-316.9, -215.5],
      [-306.2, -241.4], [-295.5, -267.3], [-284.8, -293.2], [-274.1, -319], [-263.4, -344.9],
      [-252.7, -370.8], [-242, -396.7], [-231.3, -422.6], [-220.6, -448.4], [-209.9, -474.3],
    ],
  },
  {
    id: 'redbullring',
    name: 'Red Bull Ring',
    fullName: 'Red Bull Ring',
    country: 'Austria',
    realLength: 4297,
    length: 1204,
    blurb:
      'Short and steep: three hard braking zones into Turns 1, 3 and 4 are most of the lap, with the rest spent finding the next one at altitude in the Styrian hills.',
    elevation: [[0.03, 0], [0.15, 22], [0.27, 0], [0.35, 0], [0.45, 10], [0.55, 0], [0.53, 0], [0.65, -20], [0.77, 0], [0.77, 0], [0.85, -8], [0.93, 0]],
    verticalScale: 0.6,
    runoff: [[0.32, 0.37, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
      { kind: 'bull', name: 'The bull', at: 0.13, offset: -34, scale: 1.25 },
    ],
    points: [
      [-609.2, -318.2], [-615.9, -325.6], [-618.1, -333.1], [-615.8, -340.5], [-587.3, -348.1],
      [-559.5, -351.9], [-529.7, -354.8], [-499.7, -356.4], [-471.8, -357.2], [-443.8, -356.9],
      [-413.8, -355.5], [-385.9, -353], [-356.2, -349.3], [-328.6, -344.2], [-301.1, -339.1],
      [-273.6, -333.8], [-246.1, -328.6], [-218.6, -323.4], [-191, -318.4], [-163.5, -313.3],
      [-135.9, -308.2], [-108.4, -303.2], [-80.7, -298.9], [-53, -295.2], [-23.1, -292.3],
      [4.8, -290.9], [32.8, -289.6], [60.8, -288.2], [88.8, -287], [116.7, -285.8],
      [144.7, -284.7], [164.6, -282.8], [173.6, -278.7], [182.1, -265.5], [177.7, -236.6],
      [160.8, -211.8], [141.5, -188.9], [118.4, -169.9], [92.7, -154.5], [65.2, -142.6],
      [36.1, -135.3], [6.4, -131.8], [-23.5, -133.5], [-53.2, -138.2], [-80.8, -142.7],
      [-108.4, -147.1], [-136.1, -151.5], [-163.8, -155.8], [-193.5, -159.6], [-222.7, -154],
      [-247.5, -137.5], [-266, -114.1], [-273.3, -85.2], [-269.4, -55.6], [-256.2, -28.8],
      [-242.6, -4.3], [-229.1, 20.2], [-214.6, 44.2], [-200.1, 68.2], [-183.8, 93.3],
      [-160.5, 111.8], [-132, 120.3], [-102.4, 116.9], [-75.6, 104], [-55.7, 81.8],
      [-36.8, 58.5], [-14, 39.1], [11.8, 24], [37.7, 13.3], [66.4, 5],
      [96.3, 2.5], [124.3, 2], [152.3, 1.6], [180.3, 1.1], [208.3, 0.7],
      [236.3, 0.5], [264.3, 0.4], [292.3, -0.4], [320.3, -1.2], [348.3, -2],
      [376.2, -2.7], [404.2, -3.4], [432.2, -4.1], [460.2, -4.7], [488.2, -5.4],
      [516.2, -6.1], [542, -4.6], [568.7, 8.8], [587.7, 31.7], [597.6, 60],
      [605.4, 86.9], [613.3, 113.7], [621.2, 140.6], [629.1, 167.5], [634.4, 186.7],
      [634.3, 196.7], [613.7, 217.4], [588.3, 233.2], [561.1, 246], [535.2, 256.5],
      [508.8, 265.8], [482.1, 274.3], [455.4, 282.6], [428.5, 290.4], [401.4, 297.6],
      [374.4, 304.9], [347.3, 312.2], [320.3, 319.5], [293.3, 326.7], [266.2, 334],
      [239.2, 341.3], [212.1, 348.6], [185.1, 355.8], [158.1, 363.1], [131, 370.4],
      [104, 377.6], [76.9, 384.9], [49.9, 392.2], [22.9, 399.5], [-4.2, 406.7],
      [-31.2, 414], [-58.3, 421.3], [-85.3, 428.5], [-95.2, 428.7], [-112.8, 405.3],
      [-128, 379.5], [-144.3, 356.7], [-160.7, 334], [-177.1, 311.3], [-193.5, 288.6],
      [-209.9, 265.9], [-226.4, 243.3], [-242.7, 220.5], [-258.8, 197.6], [-274, 174.1],
      [-287.6, 149.6], [-301.2, 125.1], [-313.9, 100.2], [-326.4, 75.2], [-338.8, 50],
      [-350.6, 24.7], [-362.5, -0.7], [-374.4, -26.1], [-386.4, -51.3], [-398.4, -76.6],
      [-413.3, -102.6], [-430.4, -127.3], [-446.4, -150.3], [-465.8, -173.1], [-485.4, -193.1],
      [-505, -213.1], [-524.6, -233], [-544.3, -253], [-564.1, -272.8], [-583.9, -292.5],
      [-603.6, -312.4],
    ],
  },
  {
    id: 'montreal',
    name: 'Montreal',
    fullName: 'Circuit Gilles Villeneuve',
    country: 'Canada',
    realLength: 4343,
    length: 2013,
    blurb:
      'A stop-start island lap that ends at the Wall of Champions — the exit kerb at the final chicane that has ended more than one race early.',
    elevation: [[0.3, 0], [0.5, 2], [0.7, 0]],
    runoff: [[0.85, 0.9, 'runoffAsphalt']],
    wallMargin: 5,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [220.6, 20.2], [226.6, 49.5], [231.4, 75.1], [230.9, 85], [215.6, 96.6],
      [208.4, 103.5], [209, 132.5], [215.3, 161.8], [221.5, 191.2], [227.7, 220.5],
      [233.9, 249.8], [240.2, 279.2], [246.4, 308.5], [252.6, 337.9], [258.9, 367.2],
      [265.1, 396.5], [271.3, 425.9], [277.6, 455.2], [283.8, 484.5], [290, 513.9],
      [295.9, 543.3], [299.5, 573.1], [300.9, 603], [300.3, 633], [297, 662.8],
      [293.3, 692.5], [289.6, 722.3], [286.1, 752.1], [288.2, 765.7], [312, 782.6],
      [335.5, 793.7], [344.2, 801.9], [348.2, 817], [342.3, 833.7], [326.9, 846.1],
      [297.4, 848.2], [268.3, 841.2], [239.6, 832.5], [210.9, 823.5], [183.7, 811.2],
      [158.1, 795.5], [134.4, 777.2], [111.1, 758.2], [88.3, 738.8], [65.9, 718.9],
      [43.7, 698.6], [32.7, 687.2], [29, 676], [37, 647.2], [39.4, 627.5],
      [24.6, 602], [5.6, 578.8], [-13.3, 555.5], [-31.7, 531.8], [-51.4, 509.3],
      [-76.4, 492.8], [-99.3, 473.5], [-115.4, 448.3], [-125.4, 420.1], [-129.5, 390.5],
      [-129.9, 360.5], [-129.3, 330.5], [-128.7, 300.5], [-128.1, 270.5], [-127.9, 242.5],
      [-134.2, 225.9], [-145, 217.4], [-160.7, 214.9], [-185.8, 221.4], [-209.1, 217.6],
      [-232.6, 199.3], [-248.4, 174], [-256.6, 145.2], [-262.1, 115.7], [-265.3, 85.9],
      [-267.6, 56], [-269.3, 26.1], [-271, -3.9], [-272.8, -33.8], [-273.9, -63.8],
      [-273.7, -93.8], [-272.4, -123.8], [-270.6, -153.7], [-268, -183.6], [-264.4, -213.3],
      [-260, -243], [-254.3, -272.5], [-246.9, -301.5], [-238.8, -330.4], [-230.7, -359.3],
      [-222.6, -388.1], [-214.7, -417.1], [-209.8, -432.3], [-204.3, -437.7], [-178.5, -439.5],
      [-156.7, -449], [-138.1, -472.3], [-127.6, -500.3], [-118.7, -528.9], [-110.5, -557.8],
      [-103, -586.8], [-96.2, -616], [-90, -645.4], [-84.5, -674.9], [-79.4, -704.4],
      [-74.1, -733.9], [-70.7, -763.7], [-68, -793.6], [-66.7, -823.6], [-66.6, -853.6],
      [-68.1, -883.5], [-73.2, -913], [-79.5, -942.4], [-85.7, -971.7], [-92, -1001],
      [-98.2, -1030.4], [-97.5, -1044.1], [-93.6, -1051], [-84.6, -1055.2], [-74.8, -1054.3],
      [-64.8, -1045], [-57.4, -1016], [-54.5, -986.2], [-52.5, -956.3], [-43.7, -927.6],
      [-30.9, -900.5], [-17.8, -873.6], [-5.1, -846.4], [6.1, -818.6], [17, -790.6],
      [27.8, -762.7], [38.6, -734.7], [49.5, -706.7], [60.3, -678.7], [70.6, -650.6],
      [81, -622.4], [91, -594.2], [97.4, -564.9], [103.3, -535.5], [109.2, -506.1],
      [115, -476.7], [121, -447.3], [127.3, -417.9], [133.6, -388.6], [139.9, -359.3],
      [146.1, -329.9], [152.4, -300.6], [158.7, -271.3], [165, -242], [171.3, -212.6],
      [177.5, -183.3], [183.8, -154], [190, -124.6], [196.2, -95.3], [202.4, -65.9],
      [208.6, -36.6], [214.8, -7.2],
    ],
  },
  {
    id: 'brandshatch',
    name: 'Brands Hatch',
    fullName: 'Brands Hatch Grand Prix Circuit',
    country: 'United Kingdom',
    realLength: 3888,
    length: 1100,
    blurb:
      'Paddock Hill Bend drops away from the line blind over a crest; Druids hairpin climbs straight back up the other side of the bowl it sits in.',
    elevation: [[0.01, 0], [0.05, -11], [0.09, 0], [0.07, 0], [0.12, 11], [0.17, 0], [0.35, 0], [0.5, -6], [0.65, 0]],
    verticalScale: 0.7,
    runoff: [[0.03, 0.07, 'gravel'], [0.1, 0.14, 'gravel'], [0.4, 0.45, 'gravel'], [0.6, 0.65, 'gravel']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [333.8, 526.9], [322.6, 545.6], [307.4, 554.6], [277.7, 559.1], [250, 562.8],
      [222.3, 566.7], [196.3, 566.7], [167.7, 558.1], [141.2, 548.9], [114.9, 539.5],
      [86.7, 529.3], [60.1, 515.6], [36.6, 497], [15.9, 475.3], [-2.5, 454.2],
      [-19.2, 434.3], [-23.9, 423.6], [-23.4, 393.6], [-21.7, 363.7], [-15.1, 334.5],
      [-9, 307.1], [-3, 279.8], [3.8, 252.6], [10.5, 225.5], [12.2, 201.8],
      [7.6, 188.6], [-4.5, 175.6], [-32, 164], [-58.3, 154.4], [-84.7, 144.9],
      [-111, 135.6], [-137.4, 126.2], [-163.8, 116.9], [-190.2, 107.5], [-216.6, 98.1],
      [-242.9, 88.6], [-269.3, 79.2], [-295.7, 69.8], [-322, 60.4], [-348.4, 50.9],
      [-376.5, 40.4], [-395.9, 26.7], [-412.1, 1.7], [-420.3, -27], [-418.9, -56.9],
      [-412.2, -86.1], [-400.8, -113.7], [-386.6, -140.1], [-368.2, -163.8], [-346.7, -184.7],
      [-322.2, -202.1], [-298.8, -217.4], [-274.6, -231.5], [-248.2, -245.6], [-223.2, -258.3],
      [-198.1, -270.7], [-172.8, -282.8], [-147.5, -294.8], [-122, -306.4], [-96.5, -317.9],
      [-71.1, -329.5], [-43.5, -341.4], [-15, -350.8], [14.4, -356.6], [44.2, -359.4],
      [74, -357.1], [98.6, -348.9], [120.7, -328.8], [134, -302.1], [142.3, -273.3],
      [145.3, -243.5], [147.5, -215.6], [149.1, -187.6], [148.1, -157.6], [146.8, -129.7],
      [145.4, -101.7], [143.6, -71.8], [139.9, -54.3], [128.3, -40.9], [111.6, -35],
      [95.9, -37.7], [83.6, -47.8], [76.3, -76.3], [74.8, -106.3], [73.8, -134.2],
      [72.6, -162.2], [71.1, -186.1], [61.1, -212.3], [49.4, -239.9], [43, -250],
      [26.3, -260.2], [-3.1, -257.4], [-30.1, -250.2], [-57.2, -243], [-84.2, -235.6],
      [-112.7, -226.4], [-139.8, -213.6], [-166.5, -200], [-191.2, -186.7], [-215.8, -173.4],
      [-241.5, -157.9], [-263.6, -137.9], [-277.7, -113.9], [-282.3, -94.5], [-278.6, -72.8],
      [-262.1, -51.2], [-235.3, -38.4], [-208, -32], [-180.8, -25.5], [-153.5, -19.1],
      [-126.2, -12.8], [-98.9, -6.5], [-69.8, 0.6], [-41.1, 9.4], [-13.9, 21.9],
      [11.4, 34.1], [36.5, 46.3], [61.7, 58.5], [86.9, 70.7], [112.1, 83],
      [137.4, 94.9], [162.7, 106.9], [188, 119], [213.2, 131.1], [238.5, 143.3],
      [263.7, 155.4], [288.9, 167.5], [314, 179.9], [339.2, 192.3], [365.1, 207.2],
      [387.5, 227.1], [404.3, 251.8], [412, 280.6], [411.5, 310.5], [403.2, 339.3],
      [393.6, 365.6], [384, 391.9], [374.5, 418.2], [364.8, 444.5], [355, 470.7],
      [345.1, 496.9],
    ],
  },
  {
    id: 'estoril',
    name: 'Estoril',
    fullName: 'Autódromo do Estoril',
    country: 'Portugal',
    realLength: 4176,
    length: 1565,
    blurb:
      'A lap of long, part-throttle corners that never quite lets a kart settle, closed out by the long-radius Parabólica back onto the pit straight.',
    elevation: [[0.02, 0], [0.1, 8], [0.18, 0], [0.35, 0], [0.6, -10], [0.85, 0]],
    verticalScale: 0.9,
    runoff: [[0.85, 0.9, 'runoffAsphalt']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [-58.1, -306.1], [-29.1, -313.6], [0.8, -314.6], [30.3, -309.3], [58.9, -300.4],
      [84.4, -296.1], [102.9, -302.7], [119.1, -327.3], [129.3, -355.5], [141.1, -383],
      [160.1, -406], [185, -422.6], [213.7, -431], [243.5, -430.4], [272, -421.3],
      [297.1, -405.1], [317.8, -383.4], [332.6, -357.5], [340.7, -328.6], [341.2, -298.7],
      [334.5, -269.5], [322.5, -242.1], [310.1, -214.8], [297.7, -187.5], [285.2, -160.2],
      [272.7, -132.9], [260.2, -105.6], [247.8, -78.4], [235.3, -51.1], [222.8, -23.8],
      [210.3, 3.5], [197.9, 30.8], [185.6, 58.1], [173.2, 85.4], [160.8, 112.7],
      [148.4, 140.1], [136, 167.4], [123.6, 194.7], [111.2, 222], [98.8, 249.3],
      [86.4, 276.6], [74, 303.9], [61.6, 331.2], [49.2, 358.5], [36.7, 385.8],
      [24.3, 413.1], [11.9, 440.4], [-0.6, 467.7], [-13, 495], [-25.5, 522.3],
      [-37.9, 549.6], [-50.2, 577], [-62.5, 604.3], [-75.2, 631.5], [-83, 643],
      [-95.7, 648.4], [-109.3, 646.3], [-127.1, 622.9], [-143.7, 597.9], [-163.2, 575.1],
      [-184.8, 554.3], [-207.6, 534.9], [-230.9, 515.9], [-254.2, 497.1], [-272.5, 481.6],
      [-280.5, 467.9], [-278.4, 438.4], [-269.9, 409.7], [-261.2, 380.9], [-252.2, 352.3],
      [-242.5, 324], [-232.3, 295.7], [-222, 267.6], [-215.3, 257.8], [-200.9, 251.3],
      [-183.2, 253.2], [-167.2, 264.8], [-158, 292.8], [-161.5, 322.5], [-168.2, 351.7],
      [-175.2, 380.9], [-182.3, 410], [-186.1, 437.6], [-179.7, 458.5], [-166.8, 470.5],
      [-141.5, 474.6], [-121.7, 465.8], [-107.1, 440.1], [-100.9, 410.7], [-94.9, 381.4],
      [-88.8, 352], [-82.6, 322.6], [-76.5, 293.3], [-70.5, 263.9], [-64.5, 234.5],
      [-58.2, 207.2], [-41.7, 182.4], [-23, 159], [-4.2, 135.6], [14.5, 112.1],
      [33.3, 88.7], [52, 65.3], [70.7, 41.8], [89.4, 18.4], [108.1, -5],
      [126.9, -28.5], [145.6, -51.9], [164.3, -75.3], [183.1, -98.7], [201.9, -122.1],
      [220.8, -145.4], [236.9, -165.8], [244.3, -184.2], [238.5, -208.9], [220, -226.9],
      [191.4, -235.5], [161.7, -233.6], [138.1, -223], [117.6, -201.2], [98.8, -177.9],
      [80.1, -154.5], [61.3, -131.1], [42.5, -107.7], [23.7, -84.3], [4.9, -61],
      [-13.9, -37.6], [-32.7, -14.2], [-51.5, 9.2], [-70.4, 32.4], [-86.7, 47.2],
      [-111.6, 53.1], [-138.6, 41.8], [-155.1, 17.1], [-162.3, -11.9], [-165.7, -41.7],
      [-169.1, -71.5], [-172.5, -101.3], [-173, -117.2], [-157.3, -142], [-136.2, -163.2],
      [-113.3, -182.5], [-89, -200.2], [-81.1, -209.1], [-79.4, -216.8], [-82.3, -224.1],
      [-108.4, -231.4], [-116, -233.5], [-121.1, -239.4], [-117.3, -264.5], [-97.4, -286.5],
      [-71.1, -300.9],
    ],
  },
  {
    id: 'jerez',
    name: 'Jerez',
    fullName: 'Circuito de Jerez',
    country: 'Spain',
    realLength: 4419,
    length: 1100,
    blurb:
      'Every corner here is named for a rider, not a driver — this is a MotoGP circuit first, and its tight, second-gear character shows it.',
    elevation: [[0.15, 0], [0.3, -5], [0.45, 0], [0.45, 0], [0.6, 5], [0.75, 0]],
    verticalScale: 0.9,
    runoff: [[0.02, 0.06, 'runoffAsphalt']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [132.2, 132.4], [121.3, 106.6], [110.4, 80.8], [99.5, 55], [88.6, 29.2],
      [77.7, 3.4], [66.8, -22.4], [55.9, -48.2], [45, -74], [34.1, -99.8],
      [23.2, -125.6], [12.3, -151.4], [1.4, -177.2], [-8.9, -203.3], [-19.2, -229.3],
      [-30.1, -255.1], [-41, -280.9], [-51.9, -306.7], [-62.9, -332.4], [-73.8, -358.2],
      [-84.7, -384], [-95.6, -409.8], [-98.6, -429.4], [-92.1, -448.1], [-78.8, -460],
      [-49.4, -463.9], [-21.4, -464.1], [6.7, -464.3], [34.7, -464.5], [62.7, -464.7],
      [90.7, -464.9], [112.7, -464.8], [129.2, -458.5], [137.1, -449.6], [140.3, -432.1],
      [128.7, -411.8], [106.2, -395.1], [83.9, -378.2], [67, -356.1], [58.5, -329.6],
      [59.1, -299.6], [60.6, -271.6], [62.1, -243.7], [63.6, -215.7], [66.5, -187.9],
      [75.3, -161.4], [90.1, -137.7], [111.9, -117.2], [136.3, -103.8], [163.3, -96.5],
      [191, -92.3], [218.7, -88.5], [246.5, -84.6], [274.2, -80.8], [302, -77],
      [329.7, -73.1], [357.4, -69.3], [385.2, -65.5], [412.9, -61.6], [440.1, -55.3],
      [464.4, -41.7], [483.9, -19.2], [494.5, 8.7], [495.7, 36.6], [490.9, 64.1],
      [478.3, 89], [459.5, 109.7], [437.8, 127.3], [415.8, 144.6], [393.8, 162],
      [371.8, 179.3], [349.8, 196.6], [327.8, 213.9], [305.9, 231.4], [284.1, 249],
      [262.4, 266.7], [240.6, 284.3], [218.9, 302], [197.3, 319.8], [175.6, 337.5],
      [153.7, 354.9], [131.8, 372.4], [109.9, 389.8], [88, 407.3], [66.2, 424.9],
      [44.4, 442.4], [22.6, 460], [0.6, 477.4], [-14.1, 487.7], [-29.4, 491.5],
      [-44.5, 487.1], [-55.3, 475.6], [-58, 460.3], [-47.1, 432.6], [-34.4, 407.6],
      [-21.7, 382.7], [-9, 357.7], [3.8, 332.8], [16.5, 307.8], [28.7, 282.7],
      [34.7, 255.4], [32.9, 227.6], [22, 199.7], [5.7, 177], [-11.6, 155],
      [-29, 133], [-46.3, 111.1], [-63.7, 89.1], [-81, 67.1], [-98.6, 45.3],
      [-122.5, 27.6], [-151.3, 19.8], [-181, 22.1], [-206.9, 32.5], [-229.3, 49.2],
      [-248.4, 72.2], [-260.5, 97.4], [-269.9, 123.8], [-279.2, 150.2], [-288.6, 176.6],
      [-297.9, 203], [-307.3, 229.4], [-316.9, 255.7], [-325.6, 271.3], [-342.2, 282.3],
      [-363.9, 283.5], [-390.1, 269.6], [-412.8, 253.2], [-435.4, 236.7], [-452.2, 222.6],
      [-458.2, 212.3], [-459.7, 194.6], [-448, 167.1], [-435.6, 142], [-423.2, 116.9],
      [-410.8, 91.8], [-398.4, 66.7], [-386, 41.5], [-373.6, 16.4], [-361.2, -8.7],
      [-353.9, -22.9], [-348.4, -28.6], [-336.8, -29.1], [-318.8, -20.5], [-307.1, -18.3],
      [-282.5, -33.7], [-263, -53.8], [-247.5, -69.5], [-235.2, -75.7], [-205.4, -74.2],
      [-177.5, -71.8], [-149.6, -69.4], [-123.9, -65.7], [-108.1, -57.5], [-88.3, -35.1],
      [-70.5, -13.4], [-52.8, 8.3], [-35, 29.9], [-17.3, 51.6], [0.4, 73.3],
      [18.2, 94.9], [35.9, 116.6], [53.7, 138.3], [71.4, 160], [86.1, 176.2],
      [98.7, 181.8], [116.5, 180.4], [129.6, 171.9], [136.7, 151.6],
    ],
  },
  {
    id: 'kyalami',
    name: 'Kyalami',
    fullName: 'Kyalami Grand Prix Circuit',
    country: 'South Africa',
    realLength: 4515,
    length: 1174,
    blurb:
      'Run at altitude on the Highveld: Crowthorne\'s tightening radius into the back section is the one corner that punishes carrying in too much speed.',
    elevation: [[0.05, 0], [0.15, -10], [0.25, 0], [0.3, 0], [0.55, 10], [0.8, 0]],
    verticalScale: 0.75,
    runoffDefault: 'gravel',
    runoff: [[0.9, 0.97, 'runoffAsphalt']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [70.3, 282.8], [72.8, 312.7], [74.8, 324.5], [81.9, 333.9], [94.9, 338.6],
      [110.3, 335.3], [131.8, 314.7], [152.1, 292.6], [172.3, 270.4], [192.6, 248.3],
      [212.8, 226.2], [233.1, 204.1], [253.3, 181.9], [273.6, 159.8], [293.7, 137.6],
      [313.5, 115.1], [333.4, 92.6], [353.2, 70], [372.8, 47.3], [392.2, 24.5],
      [412.6, 2.5], [433.8, -18.7], [455.6, -39.3], [479.7, -57], [504.6, -73.8],
      [529.8, -90.1], [555, -106.3], [580.2, -122.6], [605.4, -138.8], [630.7, -154.9],
      [655.9, -171.1], [681.2, -187.3], [706.3, -203.7], [731.3, -220.2], [756.9, -236],
      [776.6, -249.3], [784.2, -258.6], [787.5, -267.8], [785.8, -281.3], [779.9, -291.5],
      [753.2, -303.9], [723.7, -309.4], [694.2, -314.8], [665, -321.1], [646.3, -332.5],
      [629.2, -356.7], [615.9, -383.6], [605.3, -405.1], [583.7, -425.2], [558.2, -440.9],
      [532.5, -456.4], [516, -463.1], [497.2, -457.5], [477.4, -435.4], [459.7, -411.2],
      [442, -387], [424.3, -362.8], [406.6, -338.6], [389, -314.3], [371.4, -290],
      [353.8, -265.7], [336.3, -241.4], [318.7, -217.1], [301.2, -192.8], [283.6, -168.5],
      [266, -144.1], [248.5, -119.8], [230.9, -95.5], [212, -72.3], [188.6, -53.7],
      [161.2, -41.6], [131.9, -36.2], [102, -37.5], [73.4, -46.4], [47.5, -61.5],
      [26.3, -82.4], [8.1, -106.3], [-9.7, -130.4], [-27.3, -154.7], [-44.3, -179.4],
      [-61.4, -204.1], [-79.4, -228], [-94.5, -240.9], [-113.8, -245.1], [-136.7, -239.2],
      [-158.8, -219.3], [-176.8, -195.3], [-194.5, -171], [-212.1, -146.8], [-229.7, -122.5],
      [-247.4, -98.2], [-265, -74], [-281.6, -49.1], [-292.4, -25.5], [-293.5, -3.6],
      [-284.5, 24.8], [-271.2, 51.7], [-266.1, 81.1], [-270.5, 110.6], [-282.5, 137.9],
      [-303.6, 159.1], [-329.2, 174.6], [-356.7, 186.5], [-384.5, 197.7], [-412.3, 209],
      [-440.1, 220.2], [-468.8, 228.6], [-498.6, 231.4], [-528.5, 234.6], [-558.2, 238.8],
      [-587.1, 246.4], [-615.7, 255.4], [-644.4, 264.1], [-667.2, 271.5], [-676.4, 279.1],
      [-682.6, 289.1], [-682.6, 308.7], [-676.6, 318.9], [-651.4, 334.7], [-626, 346.4],
      [-602.6, 351], [-573.8, 343.8], [-546.4, 331.5], [-519, 319.3], [-491.7, 307],
      [-464.3, 294.8], [-436.9, 282.5], [-409.5, 270.3], [-382.2, 258], [-354.8, 245.7],
      [-327.4, 233.5], [-300.5, 220.2], [-275, 204.5], [-253.2, 184], [-234.2, 160.9],
      [-220.9, 134.1], [-212.7, 105.3], [-208.5, 75.6], [-204.6, 45.9], [-200.6, 16.1],
      [-196, -13.5], [-188.2, -42.4], [-177.2, -70.3], [-164, -97.2], [-155.2, -110.3],
      [-141.1, -117.4], [-121.4, -116.3], [-104.8, -102.7], [-90, -76.6], [-75.5, -50.4],
      [-58.8, -25.5], [-39.9, -2.2], [-18.5, 18.6], [4.1, 38.4], [28.6, 55.8],
      [44.7, 67.5], [53.5, 95.6], [56.3, 125.4], [59.1, 155.3], [61.9, 185.2],
      [64.5, 215], [67.1, 244.9], [69.6, 274.8],
    ],
  },
  {
    id: 'watkinsglen',
    name: 'Watkins Glen',
    fullName: 'Watkins Glen International',
    country: 'United States',
    realLength: 5432,
    length: 1100,
    blurb:
      'The Inner Loop\'s tight, near-doubled-back chicane — added for 1971 and still there — is the one truly slow corner on an otherwise fast, flowing upstate New York road course.',
    elevation: [[0.05, 0], [0.15, 8], [0.25, 0], [0.22, 0], [0.3, 4], [0.38, 0], [0.45, 0], [0.65, -10], [0.85, 0]],
    verticalScale: 0.8,
    runoff: [[0.02, 0.06, 'runoffAsphalt']],
    wallMargin: 7,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [-105.5, 806.6], [-105, 836.6], [-109.4, 864.2], [-125.2, 886.8], [-151.9, 899.3],
      [-179.4, 900.3], [-204.6, 889.1], [-219, 875.2], [-232.1, 848.5], [-237.7, 819.1],
      [-240.7, 789.2], [-243.3, 759.4], [-245.9, 729.5], [-248.5, 699.6], [-251, 669.7],
      [-253.6, 639.8], [-256.2, 609.9], [-258.8, 580], [-261.4, 550.2], [-264, 520.3],
      [-266.6, 490.4], [-269.2, 460.5], [-271.8, 430.6], [-274.4, 400.7], [-277, 370.8],
      [-279.6, 340.9], [-282.2, 311.1], [-282, 287.2], [-275.4, 270.7], [-251.8, 253],
      [-226.6, 248.2], [-197.5, 254.9], [-170.7, 268.3], [-143.7, 281.5], [-116.8, 294.7],
      [-89.9, 307.9], [-62.9, 321.2], [-36, 334.4], [-8.9, 347.2], [10.7, 350.4],
      [38.8, 341.2], [56.5, 325.2], [68.8, 298.4], [66.4, 269], [59.8, 239.8],
      [53.2, 210.5], [46.6, 181.2], [40, 152], [33.4, 122.7], [26.8, 93.5],
      [20.2, 64.2], [13.6, 34.9], [6.6, 5.8], [-6.3, -21.2], [-27.3, -42.4],
      [-53.8, -56.1], [-83.4, -60.5], [-113.3, -62.5], [-143.3, -64.2], [-173.2, -65.9],
      [-203.2, -67.7], [-233.1, -69.4], [-263.1, -71.2], [-288.8, -74.2], [-314.3, -89.3],
      [-332.1, -113.4], [-340.3, -142], [-339.9, -172], [-338.2, -201.9], [-336.1, -231.8],
      [-334.2, -261.8], [-332.3, -291.7], [-330.3, -321.6], [-328.4, -351.6], [-326.5, -381.5],
      [-324.6, -411.4], [-322.7, -441.4], [-320.8, -471.3], [-318.9, -501.3], [-317, -531.2],
      [-315, -561.1], [-313.1, -591.1], [-311.2, -621], [-309.3, -650.9], [-307.4, -680.9],
      [-305.5, -710.8], [-303.6, -740.8], [-301.7, -770.7], [-298.9, -800.6], [-295.7, -820.2],
      [-284.8, -834.3], [-268.9, -842.3], [-239.1, -843.3], [-209.5, -838.6], [-179.9, -833.8],
      [-150.3, -829], [-121, -822.7], [-91.7, -816.6], [-62.2, -811], [-32.8, -805.1],
      [-3.8, -797.5], [24, -786.3], [49.2, -770.3], [70.7, -749.4], [87.4, -724.5],
      [100.3, -697.5], [109.7, -669], [114.7, -639.5], [118.3, -609.7], [122.7, -580.1],
      [131.9, -551.6], [145, -524.6], [161.8, -499.8], [182.2, -477.8], [205.2, -458.6],
      [228.6, -439.9], [251.6, -420.5], [272, -398.7], [290.4, -375], [307, -350],
      [321.2, -323.6], [332.8, -295.9], [342.3, -267.5], [348.4, -238.1], [351.3, -208.3],
      [352.5, -178.4], [353.6, -148.4], [354.7, -118.4], [355.8, -88.4], [356.9, -58.4],
      [358, -28.5], [359.1, 1.5], [360.2, 31.5], [361.3, 61.5], [362.4, 91.5],
      [363.5, 121.4], [364.6, 151.4], [365.7, 181.4], [366.8, 211.4], [367.9, 241.3],
      [369, 271.3], [370.1, 301.3], [371.2, 331.3], [372.1, 361.3], [372.9, 391.3],
      [373.8, 421.2], [374.7, 451.2], [375.6, 481.2], [376.4, 511.2], [377.3, 541.2],
      [378.9, 571.1], [378.9, 601.1], [374.4, 630.6], [360.6, 657], [340.6, 679.3],
      [316.5, 697], [288.5, 706.9], [260.9, 710.8], [231.4, 706.2], [203.7, 695.1],
      [179.3, 677.9], [158.7, 656.2], [139.5, 633.2], [120.3, 610.1], [101.3, 586.9],
      [82.9, 563.2], [64.5, 539.5], [46.1, 515.9], [27.6, 492.2], [11.2, 472.1],
      [-12.4, 457.4], [-41.4, 450.4], [-71, 454.4], [-96.8, 469], [-110.8, 488.4],
      [-118.7, 517.1], [-120, 547], [-118.5, 577], [-116.8, 607], [-115.1, 636.9],
      [-113.4, 666.9], [-111.7, 696.8], [-110, 726.8], [-108.3, 756.7], [-106.6, 786.7],
    ],
  },
  {
    id: 'fuji',
    name: 'Fuji',
    fullName: 'Fuji Speedway',
    country: 'Japan',
    realLength: 4544,
    length: 1100,
    blurb:
      'The longest front straight on this list feeds a hard stop into Turn 1; 100R and the Dunlop corners on the way back keep the middle sector from ever being a rest.',
    elevation: [[0.25, 0], [0.45, 9], [0.65, 0], [0.75, 0], [0.85, -8], [0.95, 0]],
    verticalScale: 0.8,
    runoff: [[0.55, 0.6, 'gravel'], [0.65, 0.7, 'gravel']],
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [606.1, -801.1], [627.1, -819.7], [641.2, -826.6], [656.7, -824.4], [669.6, -812.5],
      [671.8, -783.3], [668, -755.5], [664.2, -727.8], [660.4, -700], [651.6, -671.5],
      [635.1, -649], [617.6, -627.1], [600.2, -605.2], [582.7, -583.3], [565.2, -561.4],
      [547.8, -539.5], [530.3, -517.6], [512.8, -495.7], [495.4, -473.9], [477.9, -452],
      [460.4, -430.1], [443, -408.2], [425.5, -386.3], [415.3, -371.5], [416.7, -342.1],
      [427.7, -316.4], [440.5, -291.6], [453.4, -266.7], [465.4, -241.4], [473.5, -214.6],
      [477.2, -186.9], [476.7, -158.9], [473.4, -131.1], [466.6, -104], [455.8, -78.2],
      [436.3, -55.7], [412.1, -41.8], [385.9, -31.9], [358.7, -25.4], [330.8, -24.6],
      [302.5, -34.1], [277.6, -50.6], [259.7, -72], [243, -94.5], [226.4, -117],
      [209.8, -139.6], [193.2, -162.1], [177.7, -183], [166.8, -191.4], [153.1, -193.2],
      [129.6, -176], [114.4, -150.3], [104.6, -122], [100.5, -94.3], [100.3, -66.3],
      [103.1, -38.5], [106.5, -10.7], [109.9, 17.1], [113.3, 44.9], [113.4, 72.9],
      [109.7, 100.6], [100, 126.8], [87.7, 151.9], [75.3, 177], [62.9, 202.2],
      [50.5, 227.3], [38.1, 252.4], [23.6, 276.3], [5.7, 297.8], [-15.1, 316.6],
      [-37.8, 332.9], [-60.5, 349.3], [-83.3, 365.6], [-106, 382], [-128.7, 398.4],
      [-151.4, 414.7], [-174.1, 431.1], [-196.9, 447.5], [-211.7, 457.6], [-219.4, 459.6],
      [-226.8, 457.2], [-233.4, 429.1], [-234.3, 413.1], [-240.9, 401], [-252.7, 393.9],
      [-282, 397.5], [-309.6, 401.1], [-338, 393], [-362.8, 379.9], [-386.9, 365.8],
      [-408.5, 348], [-427.8, 327.8], [-433.9, 311.1], [-430.8, 289.6], [-411.8, 267],
      [-387.5, 253.2], [-362.7, 240.2], [-340.6, 223.1], [-323, 199.1], [-316.7, 170.1],
      [-320.4, 142.4], [-331.6, 116.9], [-345.2, 97.1], [-354.8, 90.4], [-366.8, 89.7],
      [-390, 107.7], [-408.8, 128.5], [-427.6, 149.2], [-448.1, 168.2], [-470.4, 185.1],
      [-494.7, 199], [-523.7, 205.3], [-553.2, 200.2], [-569.4, 192.5], [-580.1, 178.3],
      [-582.9, 160.8], [-577.7, 145.8], [-555.7, 125.8], [-533.7, 108.4], [-511.8, 90.9],
      [-489.9, 73.5], [-468, 56], [-446.1, 38.6], [-424.2, 21.1], [-402.3, 3.7],
      [-380.4, -13.8], [-358.5, -31.3], [-336.7, -48.7], [-314.8, -66.2], [-292.9, -83.7],
      [-271, -101.1], [-249.1, -118.6], [-227.2, -136.1], [-205.3, -153.5], [-183.4, -171],
      [-161.5, -188.5], [-139.7, -205.9], [-117.8, -223.4], [-95.9, -240.9], [-74, -258.3],
      [-52.1, -275.8], [-30.2, -293.3], [-8.3, -310.7], [13.6, -328.2], [35.5, -345.7],
      [57.3, -363.1], [79.2, -380.6], [101.1, -398.1], [123, -415.5], [144.9, -433],
      [166.8, -450.5], [188.7, -467.9], [210.6, -485.4], [232.5, -502.9], [254.3, -520.3],
      [276.2, -537.8], [298.1, -555.3], [320, -572.7], [341.9, -590.2], [363.8, -607.7],
      [385.7, -625.1], [407.6, -642.6], [429.5, -660.1], [451.3, -677.5], [473.2, -695],
      [495.1, -712.5], [517, -729.9], [538.9, -747.4], [560.8, -764.9], [582.7, -782.3],
    ],
  },
  {
    id: 'sochi',
    name: 'Sochi',
    fullName: 'Sochi Autodrom',
    country: 'Russia',
    realLength: 5846,
    length: 1568,
    blurb:
      'Built on the Olympic Park\'s own access roads rather than a purpose-built circuit — OSM has no through-route traced for it, so this is reconstructed from the published layout rather than surveyed. Turn 3 is a single huge-radius sweeper taken nearly flat.',
    wallMargin: 5,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [0, 0], [27.9, -1.8], [55.9, -3.6], [83.8, -5.5], [111.8, -7.3],
      [139.7, -9.1], [167.6, -10.9], [195.6, -12.8], [223.5, -14.6], [251.5, -16.4],
      [279.4, -18.2], [307.3, -20], [335.3, -21.9], [363.2, -23.7], [391.2, -25.5],
      [419.1, -27.3], [447.1, -29.2], [475, -31], [502.9, -32.8], [530.9, -34.6],
      [558.8, -36.4], [586.8, -38.3], [614.7, -40.1], [642.6, -41.9], [670.6, -43.7],
      [698.5, -45.6], [726.5, -47.4], [754.4, -49.2], [782.3, -51], [810.3, -52.8],
      [838.2, -54.7], [866.2, -56.5], [894.1, -58.3], [922, -60.1], [950, -62],
      [977.9, -63.8], [1007.9, -65.8], [1037.4, -70.5], [1065.9, -79.8], [1092.9, -92.9],
      [1117.9, -105.7], [1140.5, -118.4], [1158.3, -142.1], [1162.7, -171.5], [1160.9, -199.4],
      [1158.7, -229.3], [1155.2, -259.1], [1150.3, -288.7], [1144.1, -318.1], [1136.5, -347.1],
      [1127.6, -375.7], [1117.4, -403.9], [1105.9, -431.6], [1093.1, -458.8], [1079.2, -485.3],
      [1064, -511.2], [1047.7, -536.4], [1030.3, -560.8], [1011.8, -584.4], [992.2, -607.2],
      [971.7, -629], [950.2, -649.9], [927.8, -669.9], [904.5, -688.8], [880.4, -706.7],
      [855.5, -723.4], [829.9, -739], [803.6, -753.5], [776.7, -766.8], [749.3, -778.9],
      [721.3, -789.7], [692.8, -799.2], [664, -807.4], [634.8, -814.3], [605.3, -819.9],
      [575.6, -824.2], [545.8, -827], [515.8, -828.5], [485.8, -828.6], [455.8, -827.2],
      [426, -824.5], [396.3, -820.4], [366.8, -814.9], [337.6, -807.9], [308.7, -800],
      [281.7, -792.5], [254.7, -785], [227.7, -777.5], [200.7, -770.1], [173.7, -762.6],
      [146.8, -755.1], [117.3, -750], [87.7, -754.2], [60.9, -767.3], [37.2, -785.7],
      [13.6, -804.2], [-10, -822.7], [-33.7, -841.3], [-57.4, -859.6], [-84.4, -872.3],
      [-114.1, -875.2], [-143, -867.6], [-168.3, -855.7], [-195.5, -842.9], [-220.8, -831],
      [-248.6, -820], [-278.3, -816.3], [-307.9, -820.6], [-336.2, -830.4], [-364.5, -840.6],
      [-390.8, -850.1], [-417.1, -859.6], [-445.4, -869.8], [-471.7, -879.3], [-500.6, -886.9],
      [-530.2, -883.3], [-555.5, -867.7], [-571.9, -842.8], [-583.9, -817.6], [-595.9, -792.3],
      [-607.9, -767], [-621.6, -740.3], [-640.9, -717.5], [-665.4, -700.2], [-692.7, -688],
      [-720.3, -676.2], [-746, -665.1], [-773.6, -653.3], [-801.2, -641.5], [-828.1, -628.3],
      [-850, -608.1], [-863.8, -581.6], [-869.2, -552.1], [-873.4, -524.4], [-877.9, -494.8],
      [-884.8, -465.7], [-902.6, -441.8], [-928.4, -427], [-957.9, -422.3], [-987.9, -420.1],
      [-1017.8, -417.9], [-1047.6, -414.6], [-1076.4, -406.3], [-1103.1, -392.8], [-1126.8, -374.4],
      [-1148.1, -356.2], [-1169.3, -338], [-1190.6, -319.8], [-1213.4, -300.3], [-1236.2, -280.8],
      [-1259, -261.3], [-1281.8, -241.8], [-1303, -223.5], [-1310.8, -212.1], [-1311.9, -198.3],
      [-1306.7, -187.7], [-1297, -180.9], [-1267.2, -178.2], [-1237.2, -176.8], [-1207.3, -175.3],
      [-1177.3, -173.9], [-1147.3, -172.5], [-1117.4, -171], [-1087.4, -169.6], [-1057.4, -168.2],
      [-1027.5, -166.7], [-997.5, -165.3], [-967.5, -163.9], [-937.6, -162.4], [-907.6, -161],
      [-877.6, -159.6], [-847.7, -158.1], [-817.7, -156.7], [-787.8, -155.3], [-757.8, -153.8],
      [-727.8, -152.4], [-697.9, -151], [-667.9, -149.5], [-637.9, -148.1], [-608, -146.7],
      [-578, -145.2], [-548, -143.8], [-518.1, -142.4], [-488.1, -140.9], [-458.1, -139.5],
      [-428.2, -138.1], [-398.2, -136.6], [-368.2, -135.2], [-338.3, -133.8], [-308.3, -132.3],
      [-278.3, -130.9], [-248.4, -129.5], [-218.4, -128], [-188.4, -126.6], [-158.5, -124.8],
      [-130, -116], [-106.3, -97.9], [-89.5, -73.1], [-75.5, -48.9], [-59.2, -23.8],
      [-35.1, -6.2], [-6, 0.1],
    ],
  },
  {
    id: 'longbeach',
    name: 'Long Beach',
    fullName: 'Long Beach Street Circuit',
    country: 'United States',
    realLength: 3167,
    length: 1100,
    blurb:
      'A street circuit run on public roads that are never tagged as a raceway between race weekends, so this too is built from the known layout rather than traced. The Aquarium hairpin is the tightest corner either way round this lap.',
    elevation: [[0.4, 0], [0.6, 1], [0.8, 0]],
    runoff: [[0.55, 0.62, 'runoffAsphalt']],
    wallMargin: 5,
    landmarks: [
      { kind: 'grandstand', name: 'Main grandstand', at: 0.97, offset: 28, roomier: true },
    ],
    points: [
      [0, 0], [21.1, -6.2], [42.2, -12.4], [63.3, -18.5], [84.4, -24.7],
      [105.5, -30.9], [126.6, -37.1], [147.8, -43.2], [168.9, -49.4], [190, -55.6],
      [211.1, -61.8], [232.2, -67.9], [253.3, -74.1], [274.4, -80.3], [295.5, -86.5],
      [316.6, -92.6], [337.7, -98.8], [358.8, -105], [379.9, -111.2], [401.1, -117.4],
      [422.2, -123.5], [443.3, -129.7], [464.4, -135.9], [485.5, -142.1], [506.6, -148.2],
      [527.7, -154.4], [548.8, -160.6], [569.9, -166.8], [591, -172.9], [612.1, -179.1],
      [633.2, -185.3], [654.3, -191.5], [675.5, -197.6], [696.6, -203.8], [717.7, -210],
      [738.8, -216.2], [759.9, -222.3], [781, -228.5], [802.1, -234.7], [823.2, -240.9],
      [844.3, -247.1], [865.4, -253.2], [886.5, -259.4], [907.6, -265.6], [928.8, -271.8],
      [949.9, -277.9], [971, -284.1], [992.1, -290.3], [1013.2, -296.5], [1034.3, -302.6],
      [1054, -312], [1066.9, -329.5], [1070.6, -351], [1064.6, -371.9], [1050.3, -388.4],
      [1034.5, -403.7], [1018.7, -419], [1002.8, -434.2], [987, -449.5], [971.1, -464.7],
      [955.3, -480], [939.5, -495.3], [923.6, -510.5], [907.8, -525.8], [891.8, -540.9],
      [874.7, -554.7], [856.5, -567], [837.2, -577.5], [816.9, -586], [795.9, -592.4],
      [774.2, -596.3], [752.3, -597.5], [730.4, -595.9], [708.9, -591.1], [688.5, -583],
      [668.6, -573.7], [648.6, -564.5], [628.6, -555.3], [608.7, -546], [588.7, -536.8],
      [568.8, -527.5], [548.8, -518.3], [528.9, -509], [508.9, -499.8], [488.9, -490.6],
      [469, -481.3], [449, -472.1], [429.1, -462.8], [409.1, -453.6], [389.2, -444.3],
      [369.2, -435.1], [349.3, -425.9], [329.3, -416.6], [309.3, -407.4], [289.3, -398.3],
      [268, -393.5], [246.1, -394.7], [224.6, -399.4], [203.1, -404.2], [181.6, -409],
      [160.2, -413.8], [138.7, -418.6], [117.2, -423.2], [95.3, -424], [74, -418.9],
      [55.7, -407], [41.3, -390.4], [27.3, -373.4], [13.3, -356.5], [-0.7, -339.5],
      [-14.8, -322.6], [-28.8, -305.7], [-43.4, -289.2], [-62.3, -278.3], [-83.8, -273.7],
      [-105.6, -270.6], [-127.3, -267.5], [-149.1, -264.4], [-170.9, -261.3], [-192.6, -258.2],
      [-214.2, -254.2], [-233, -243.1], [-243.9, -224.3], [-251, -203.5], [-258, -182.6],
      [-265.1, -161.8], [-272.2, -141], [-279.2, -120.2], [-286.1, -99.3], [-286.4, -77.5],
      [-277.2, -57.7], [-261.2, -42.9], [-243.1, -30.4], [-224.9, -18], [-206.8, -5.6],
      [-188.6, 6.9], [-170.2, 18.8], [-149.7, 26.7], [-128, 29.9], [-106, 28.9],
      [-84.6, 24.2], [-63.4, 18.1], [-42.3, 12.1], [-21.1, 6],
    ],
  },
];

export function circuitKey(id: string): string {
  return CIRCUIT_KEY_PREFIX + id;
}

/** The circuit a track key names, or null if the key is a date or a seed. */
export function circuitForKey(key: string): RealCircuit | null {
  if (!key.startsWith(CIRCUIT_KEY_PREFIX)) return null;
  const id = key.slice(CIRCUIT_KEY_PREFIX.length);
  return REAL_CIRCUITS.find((c) => c.id === id) ?? null;
}
